import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "troupeo:lang";

// WhatsApp number — Haithem Sakkal (founder Mazra3ti), accepts international format.
// Format: digits only with country code, no '+' or spaces (used in wa.me link).
export const WHATSAPP_NUMBER = "33777008433";
export const WHATSAPP_DISPLAY = "+33 7 77 00 84 33";

const fr = {
  dir: "ltr",
  brand: {
    name: "Mazra3ti",
    tagline: "Gérez votre élevage simplement.",
  },
  nav: {
    features: "Fonctionnalités",
    pricing: "Tarifs",
    faq: "FAQ",
    contact: "Contact",
    demo: "Demander une démo",
    waitlist: "Liste d'attente",
    langSwitch: "العربية",
    langSwitchAria: "Passer en arabe",
  },
  hero: {
    overline: "L'APPLICATION DES ÉLEVEURS",
    headline_a: "Gérez votre élevage",
    headline_b: "simplement.",
    subheadline:
      "Suivez la santé, la reproduction et l'alimentation de votre troupeau en temps réel. L'outil indispensable pour moderniser votre ferme, depuis l'étable jusqu'au pré.",
    cta_primary: "Créer mon compte — 1 mois gratuit",
    cta_login: "Espace client — Connexion",
    cta_dashboard: "Mon espace Mazra3ti",
    email_placeholder: "Votre adresse e-mail",
    submit: "Rejoindre la liste",
    submitting: "Inscription...",
    trust_hosting: "Données chiffrées & sécurisées",
    trust_offline: "Fonctionne hors-ligne",
    trust_trial: "Essai gratuit, sans engagement",
    stat_card_label: "Tête de bétail suivie",
    stat_card_sub: "+12 nouveaux animaux ce mois-ci",
    stats: [
      ["1 200+", "éleveurs en attente"],
      ["24", "gouvernorats couverts"],
      ["94%", "satisfaction bêta"],
      ["0", "papier perdu"],
    ],
  },
  features: {
    overline: "Fonctionnalités",
    headline_a: "Tout ce dont vous avez besoin,",
    headline_b: "au même endroit.",
    description:
      "Une plateforme conçue avec et pour les éleveurs. Conçue pour les mains gantées, pensée pour gagner du temps chaque jour.",
    items: {
      sante: {
        title: "Suivi sanitaire intelligent",
        description:
          "Carnet sanitaire numérisé, traçabilité complète, alertes vaccins automatisées et rappels personnalisés pour chaque animal.",
      },
      reproduction: {
        title: "Reproduction",
        description: "Anticipation des chaleurs et suivi des gestations.",
      },
      alimentation: {
        title: "Alimentation",
        description:
          "Optimisation des rations et gestion des silos avec calculs nutritionnels.",
      },
      mobile: {
        title: "100% Mobile",
        description: "Accessible hors-ligne, directement depuis le pré.",
      },
      analyses: {
        title: "Analyses & rentabilité",
        description:
          "Tableaux de bord clairs pour piloter votre exploitation.",
      },
    },
  },
  trainer: {
    overline: "Conseiller fondateur",
    headline_a: "Accompagné par",
    headline_b: "Haithem,",
    headline_c: "votre conseiller Mazra3ti.",
    school: "Mazra3ti",
    school_full: "Mazra3ti — Plateforme dédiée aux éleveurs",
    description:
      "Haithem Sakkal, fondateur de Mazra3ti, j'accompagne personnellement chaque éleveur dans l'utilisation de la plateforme. Je suis joignable directement par WhatsApp pour toute question technique ou commerciale.",
    perks: [
      "Activation instantanée de votre compte",
      "1 mois d'essai gratuit, sans engagement",
      "Accompagnement personnalisé du fondateur",
      "Support technique direct par WhatsApp",
    ],
    education_title: "Diplômé du centre de formation des experts",
    education_school: "A.C.A Consult — Sfax",
    competencies: [
      "Élevage ovin",
      "Taille de l'olivier",
      "Formulation & fabrication de concentré animalier",
      "Techniques de moulin à huile",
      "Export à l'international",
    ],
    cta_whatsapp: "Contacter Haithem sur WhatsApp",
    cta_call: "Appeler",
    badge: "Fondateur",
    team_overline: "L'équipe Mazra3ti",
    team_title: "Une équipe à votre écoute",
    team_desc: "Au-delà du support technique, vous bénéficiez de l'expertise d'un ingénieur spécialisé pour toutes vos questions d'élevage.",
    team_disclaimer: "Les conseils prodigués par notre équipe concernent uniquement la conduite d'élevage, la nutrition et les bonnes pratiques. Pour toute prescription médicamenteuse ou diagnostic vétérinaire, consultez un vétérinaire diplômé.",
    team_members: [
      {
        name: "Seifeddine Ben Rejeb",
        role: "Ingénieur agronome & animalier",
        bio: "Vous accompagne sur la conduite du troupeau : nutrition, rations, reproduction, gestion technique et bonnes pratiques d'élevage. Pour toute question médicale, il vous orientera vers un vétérinaire.",
        initials: "SB",
        photo: "/seifeddine-avatar.jpg",
        education: ["Le Kef · Tunisie", "Italie · Digestion animale"],
        scope_in: ["Nutrition & rations", "Reproduction technique", "Conduite du troupeau", "Bonnes pratiques"],
        scope_out: ["Prescription médicamenteuse", "Diagnostic vétérinaire", "Actes médicaux"],
      },
    ],
  },
  pricing: {
    overline: "Tarifs",
    headline: "Des tarifs adaptés à votre exploitation.",
    description:
      "Sans engagement, résiliable à tout moment. Un mois gratuit pour tout nouvel élevage.",
    unit: "/ mois",
    popular: "Le plus populaire",
    on_quote: "Sur devis",
    plans: {
      essential: {
        name: "Essentiel",
        price: "5 DT",
        badge: "1er mois offert",
        description: "Pour les petits élevages qui commencent à numériser.",
        features: [
          "Jusqu'à 50 têtes",
          "Carnet sanitaire numérique",
          "Application mobile hors-ligne",
          "Support par e-mail",
        ],
        note: "Modules supplémentaires disponibles en option payante.",
        cta: "Essayer gratuitement",
      },
      pro: {
        name: "Pro",
        price: "15 DT",
        description: "Pour les éleveurs exigeants et en croissance.",
        features: [
          "Animaux illimités",
          "Alertes reproduction & chaleurs",
          "Gestion des stocks & rations",
          "Analyses de rentabilité",
          "5 questions + 5 appels/mois avec Seifeddine (conseil technique d'élevage)",
          "Support prioritaire",
        ],
        cta: "Commencer maintenant",
      },
      domain: {
        name: "Domaine",
        description: "Pour les grandes structures et coopératives.",
        features: [
          "Multi-sites & multi-utilisateurs",
          "API & intégrations comptables",
          "Conseils techniques illimités avec Seifeddine (élevage, nutrition)",
          "Accompagnement dédié",
          "SLA garanti",
        ],
        cta: "Contacter les ventes",
      },
    },
  },
  faq: {
    overline: "FAQ",
    headline: "Questions fréquentes",
    hint_a: "Une question qui n'est pas listée ?",
    hint_link: "Écrivez-nous",
    items: [
      {
        q: "L'application fonctionne-t-elle sans internet ?",
        a: "Oui, Mazra3ti dispose d'un mode hors-ligne complet. Vous pouvez saisir une pesée ou un traitement au fond du pré, vos données se synchronisent automatiquement dès que vous retrouvez du réseau.",
      },
      {
        q: "Mes données sont-elles sécurisées et privées ?",
        a: "Absolument. Vos données sont chiffrées de bout en bout (HTTPS + chiffrement des mots de passe), hébergées sur une infrastructure cloud sécurisée, et vous appartiennent à 100%. Vous pouvez les exporter ou les supprimer à tout moment.",
      },
      {
        q: "Puis-je exporter mon carnet sanitaire pour mon vétérinaire ?",
        a: "Oui, vous pouvez exporter vos registres en PDF ou Excel en un clic. Vous pouvez aussi inviter votre vétérinaire en lecture seule pour qu'il consulte les fiches santé directement.",
      },
      {
        q: "Combien de temps faut-il pour démarrer ?",
        a: "La plupart des éleveurs sont opérationnels en moins d'une heure. Nous proposons un import gratuit de vos données existantes (Excel, papier, autre logiciel) et un accompagnement téléphonique.",
      },
      {
        q: "Quels types d'élevage sont supportés ?",
        a: "Bovins (lait & viande), ovins, caprins, lapins, équins. Si votre filière n'est pas listée, contactez-nous : nous adaptons régulièrement la plateforme aux besoins du terrain.",
      },
      {
        q: "Y a-t-il un engagement de durée ?",
        a: "Aucun engagement. Vous pouvez résilier votre abonnement en un clic, et vos données restent exportables pendant 12 mois après la résiliation.",
      },
    ],
  },
  contact: {
    overline: "Nous contacter",
    headline: "Prêt à moderniser votre ferme ?",
    description:
      "Activation instantanée : créez votre compte et profitez d'1 mois gratuit, sans engagement. Une question ? Contactez-nous directement par WhatsApp.",
    signup_cta: "Créer mon compte — 1 mois gratuit",
    login_cta: "J'ai déjà un compte",
    waitlist_placeholder: "Votre adresse e-mail",
    waitlist_submit: "Je m'inscris",
    email: "contact@mazra3ti.tn",
    phone: "+216 71 000 000",
    address: "Tunis — Tunisie",
    form_title: "Demander une démo",
    form_subtitle:
      "Décrivez votre exploitation, nous revenons vers vous rapidement.",
    labels: {
      name: "Nom complet",
      email: "Adresse e-mail",
      farm_type: "Type d'élevage",
      message: "Votre message",
    },
    placeholders: {
      name: "Mohamed Ben Ali",
      email: "mohamed@ferme.tn",
      farm_type: "Sélectionnez votre filière",
      message: "Parlez-nous de votre élevage et de vos besoins…",
    },
    farm_options: {
      Bovins: "Bovins",
      Ovins: "Ovins",
      Caprins: "Caprins",
      Lapins: "Lapins",
      Autre: "Autre",
    },
    disclaimer:
      "En envoyant ce formulaire, vous acceptez d'être recontacté par notre équipe.",
    submit: "Envoyer le message",
    submitting: "Envoi…",
  },
  footer: {
    tagline:
      "La plateforme moderne pour gérer votre élevage, du carnet sanitaire à la rentabilité de votre exploitation.",
    col_product: "Produit",
    col_company: "Entreprise",
    rights: "Tous droits réservés",
    legal: "Mentions légales",
    privacy: "Confidentialité",
    cgv: "CGV",
  },
  toasts: {
    wait_success_title: "Inscription réussie !",
    wait_success_desc: "Vous êtes sur la liste d'attente de Mazra3ti.",
    wait_success_desc_short: "Nous vous tenons informé du lancement.",
    wait_dup_title: "Déjà inscrit",
    wait_dup_desc: "Cet email figure déjà sur la liste d'attente.",
    error_title: "Erreur",
    error_desc: "Impossible de vous inscrire pour le moment.",
    missing_title: "Champs manquants",
    missing_desc: "Merci de remplir tous les champs.",
    contact_success_title: "Message envoyé !",
    contact_success_desc: "Notre équipe vous répond sous 24h ouvrées.",
    contact_error_desc: "Impossible d'envoyer le message pour le moment.",
  },
  app: {
    common: {
      loading: "Chargement…",
      save: "Enregistrer",
      saving: "Enregistrement…",
      update: "Mettre à jour",
      updating: "Mise à jour…",
      cancel: "Annuler",
      edit: "Modifier",
      delete: "Supprimer",
      deleting: "Suppression…",
      create: "Créer",
      creating: "Création…",
      logout: "Déconnexion",
      back: "Retour",
      back_home: "Retour à l'accueil",
      actions: "Actions",
      search: "Rechercher",
      filter: "Filtrer",
      yes: "Oui",
      no: "Non",
      view_all: "Voir tout",
      lang_toggle: "العربية",
      lang_toggle_aria: "Passer en arabe",
      irreversible: "Cette action est irréversible.",
      no_results: "Aucun résultat.",
      none: "—",
    },
    role: { owner: "Propriétaire", trainer: "Formateur", client: "Éleveur" },
    status: { active: "Actif", pending: "En attente", suspended: "Suspendu", sold: "Vendu", dead: "Décédé" },
    shell: {
      overview: "Vue d'ensemble",
      clients: "Clients",
      trainers: "Formateurs",
      audit: "Journal d'audit",
      security: "Sécurité (2FA)",
      landing: "Voir la landing page",
      expert_inbox: "File experts",
    },
    owner: {
      overline: "Espace propriétaire",
      welcome: "Bienvenue",
      subtitle: "Vue d'ensemble de votre activité Mazra3ti.",
      view_all_clients: "Tous les clients",
      stat_total: "Clients totaux",
      stat_active: "Actifs",
      stat_pending: "En attente",
      stat_suspended: "Suspendus",
      mrr: "Revenus mensuels récurrents",
      per_month: "/ mois",
      annual_estimate: "≈ {n} DT / an si stable",
      commission_said: "Commission conseiller (50%)",
      net_owner: "Net pour vous",
      plan_breakdown: "Répartition par plan",
      recent_title: "5 derniers clients",
      no_clients: "Aucun client pour l'instant.",
    },
    trainer: {
      overline: "Espace formateur",
      hello: "Bonjour",
      subtitle: "Vos clients, leur activation et votre commission, en un coup d'œil.",
      new_client: "Nouveau client",
      signup_banner_count_one: "{n} nouveau compte en attente d'activation",
      signup_banner_count_many: "{n} nouveaux comptes en attente d'activation",
      signup_banner_sub: "Un éleveur s'est inscrit depuis le site. Activez son compte en 1 clic.",
      stat_total: "Total clients",
      mrr_label: "Revenu mensuel récurrent",
      mrr_sub: "Sur les clients actifs visibles",
      commission_label: "Votre commission (50%)",
      commission_sub: "Estimée ce mois",
      manage_clients: "Gérer mes clients",
      recent_title: "Derniers comptes créés",
      no_clients: "Aucun client pour l'instant. Créez votre premier compte.",
    },
    client: {
      hello: "Bonjour",
      subtitle: "Votre espace Mazra3ti pour gérer votre élevage.",
      pending_title: "Activez votre compte",
      pending_desc: "Votre compte est en attente. Vous pouvez l'activer immédiatement par paiement en ligne sécurisé, ou nous contacter sur WhatsApp pour finaliser.",
      pay_now: "Payer en ligne maintenant",
      suspended_title: "Votre essai gratuit est terminé",
      suspended_desc: "Pour continuer à utiliser Mazra3ti, activez votre abonnement par paiement en ligne sécurisé. Vous gardez l'accès à toutes vos données.",
      active_status: "Compte actif",
      active_plan: "Plan {plan}, paiement {cycle}",
      monthly: "mensuel",
      annual: "annuel",
      modules_intro: "Bienvenue chez Mazra3ti. Voici les modules disponibles aujourd'hui.",
      mod_animals_title: "Registre des animaux",
      mod_animals_desc: "Ajoutez et suivez chaque tête de bétail : identification, espèce, race, sexe, âge, poids.",
      mod_health_title: "Carnet sanitaire",
      mod_health_desc: "Vaccins, traitements, visites vétérinaires et rappels — pour chaque animal.",
      open: "Ouvrir",
    },
    clients_list: {
      overline_owner: "Tous les clients",
      overline_trainer: "Mes clients",
      overline_signup: "Nouveaux comptes en attente",
      title_clients: "Clients",
      title_signup: "Auto-inscriptions",
      view_all: "← Voir tous les clients",
      search_placeholder: "Rechercher par nom ou email…",
      th_name: "Nom",
      th_email: "Email",
      th_plan: "Plan",
      th_billing: "Fréquence",
      th_status: "Statut",
      th_rate: "Tarif effectif",
      billing_monthly: "Mensuel",
      billing_annual: "Annuel (-20%)",
      empty_first: "Aucun client. Créez le premier compte ci-dessus.",
      empty_search: "Aucun résultat pour cette recherche.",
      self_signup_badge: "Auto-inscrit",
      self_signup_title: "Inscrit depuis la landing",
      action_activate: "Activer le compte",
      action_suspend: "Suspendre",
      action_pending: "Marquer en attente",
      action_whatsapp: "Contacter sur WhatsApp",
      new_client: "Nouveau client",
      create_title: "Créer un compte éleveur",
      create_desc: "Le client pourra se connecter avec l'email et le mot de passe ci-dessous.",
      full_name: "Nom complet",
      email: "Email",
      initial_password: "Mot de passe initial",
      min6: "Min. 6 caractères",
      billing: "Fréquence",
      initial_status: "État initial du compte",
      status_active_paid: "Actif (paiement reçu)",
      status_pending_paid: "En attente de paiement",
      annual_minus_20: "Annuel (-20% de remise)",
      tarif_summary: "Récapitulatif tarifaire",
      tarif_annual: "Annuel : {a} DT (au lieu de {b} DT) — économie {c} DT",
      tarif_monthly: "Mensuel : {a} DT / mois",
      create_btn: "Créer le compte",
      status_updated: "Statut mis à jour",
      client_created: "Client créé",
      client_created_desc: "{name} peut maintenant se connecter.",
    },
    trainers_list: {
      overline: "Gestion des formateurs",
      title: "Formateurs",
      subtitle: "Suspendez un formateur ou réinitialisez son mot de passe en cas de besoin. Ses clients restent visibles dans votre dashboard.",
      th_clients: "Clients",
      empty: "Aucun formateur.",
      suspend: "Suspendre le formateur",
      reactivate: "Réactiver",
      reset_password: "Réinitialiser le mot de passe",
      new_trainer: "Nouveau formateur",
      create_title: "Créer un formateur partenaire",
      create_desc: "Le formateur recevra ces identifiants. Il devra changer son mot de passe à la 1re connexion.",
      password_hint: "Mot de passe initial (min 10, MAJ, chiffre, spécial)",
      create_btn: "Créer le formateur",
      reset_title: "Réinitialiser le mot de passe",
      reset_desc: "Vous allez définir un nouveau mot de passe pour {name}. Son ancien mot de passe ne fonctionnera plus.",
      new_password: "Nouveau mot de passe",
      reset_btn: "Réinitialiser",
      suspended_toast: "Formateur suspendu",
      reactivated_toast: "Formateur réactivé",
      created_toast: "Formateur créé",
      reset_toast: "Mot de passe réinitialisé",
      reset_toast_desc: "{name} devra utiliser le nouveau mot de passe.",
    },
    audit: {
      overline: "Journal d'audit",
      title: "Activité administrative",
      subtitle: "Toutes les actions sensibles (création, activation, suspension, changement de mot de passe) sont tracées ici.",
      filter_placeholder: "Filtrer par email, action, IP…",
      th_date: "Date",
      th_actor: "Acteur",
      th_action: "Action",
      th_target: "Cible",
      th_ip: "IP",
      empty: "Aucune entrée.",
      actions: {
        create_client: "Création de client",
        create_trainer: "Création de formateur",
        client_active: "Client activé",
        client_pending: "Client en attente",
        client_suspended: "Client suspendu",
        trainer_active: "Formateur réactivé",
        trainer_suspended: "Formateur suspendu",
        reset_trainer_password: "Reset mot de passe formateur",
        change_password: "Changement de mot de passe",
        self_signup: "Auto-inscription",
        payment_activation: "Activation paiement",
        "2fa_enabled": "2FA activée",
        "2fa_disabled": "2FA désactivée",
        "2fa_backup_code_used": "Code de secours 2FA utilisé",
      },
    },
    security: {
      overline: "Sécurité",
      title: "Authentification à deux facteurs",
      subtitle: "Ajoutez une seconde barrière : un code à 6 chiffres généré par une application sur votre téléphone à chaque connexion.",
      status_on: "2FA est activée sur votre compte",
      status_off: "2FA n'est pas encore activée",
      desc_on: "Vos connexions sont protégées par un code à 6 chiffres temporaire.",
      desc_off: "Nous vous recommandons fortement d'activer la 2FA, surtout en Tunisie où le risque de piratage est élevé.",
      enable: "Activer la 2FA",
      disable: "Désactiver la 2FA",
      step1: "Étape 1",
      step1_title: "Scannez ce QR code",
      step1_desc: "Ouvrez Google Authenticator, Authy ou Microsoft Authenticator sur votre téléphone et scannez ce code.",
      manual_key: "Ou saisissez la clé manuellement",
      step2: "Étape 2",
      step2_title: "Entrez le code à 6 chiffres",
      step2_desc: "Une fois le compte ajouté dans votre app, entrez le code généré pour confirmer.",
      activating: "Activation…",
      activate_btn: "Activer la 2FA",
      backup_title: "Vos codes de récupération — à conserver maintenant",
      backup_desc_a: "Ces 8 codes vous permettent de vous connecter si vous perdez votre téléphone. Chaque code n'est utilisable qu'une seule fois. ",
      backup_desc_b: "Ils ne seront plus jamais affichés.",
      download_txt: "Télécharger en .txt",
      saved_dismiss: "J'ai sauvegardé les codes",
      disable_title: "Désactiver la 2FA",
      disable_desc: "Cette action réduit la sécurité de votre compte. Vous devrez la réactiver pour bénéficier à nouveau de cette protection.",
      current_password: "Mot de passe actuel",
      current_code: "Code 2FA actuel ou code de récupération",
      disable_btn: "Désactiver",
      toast_enabled: "2FA activée",
      toast_disabled: "2FA désactivée",
    },
    animals: {
      overline: "Mon troupeau",
      title: "Registre des animaux",
      subtitle: "Suivez chaque tête de bétail : identification, espèce, âge, poids et statut.",
      new_animal: "Nouvel animal",
      add_title: "Ajouter un animal",
      edit_title: "Modifier l'animal",
      th_tag: "Tag",
      th_name: "Nom",
      th_species_breed: "Espèce / Race",
      th_sex: "Sexe",
      th_birth: "Naissance",
      th_weight: "Poids",
      th_status: "Statut",
      species: { Bovins: "Bovins", Ovins: "Ovins", Caprins: "Caprins", Lapins: "Lapins", Autre: "Autre" },
      all_species: "Toutes espèces",
      female: "Femelle",
      male: "Mâle",
      stat_total: "Total",
      stat_active: "Actifs",
      stat_sold: "Vendus",
      stat_dead: "Décédés",
      empty_first: "Aucun animal enregistré. Cliquez sur « Nouvel animal » pour commencer.",
      empty_search: "Aucun résultat avec ces filtres.",
      search_placeholder: "Rechercher (tag, nom, race)…",
      tag_label: "Identifiant / Tag*",
      name_label: "Nom (optionnel)",
      species_label: "Espèce*",
      breed_label: "Race",
      sex_label: "Sexe*",
      birth_label: "Date de naissance",
      weight_label: "Poids (kg)",
      status_label: "Statut",
      notes_label: "Notes",
      notes_placeholder: "Observations…",
      delete_title: "Supprimer cet animal ?",
      saved_toast: "Animal mis à jour",
      created_toast: "Animal enregistré",
      deleted_toast: "Animal supprimé",
    },
    health: {
      overline: "Santé du troupeau",
      title: "Carnet sanitaire",
      subtitle: "Vaccins, traitements, visites vétérinaires et rappels — pour chaque animal.",
      new_record: "Nouvel enregistrement",
      add_title: "Ajouter au carnet sanitaire",
      edit_title: "Modifier l'enregistrement",
      add_animal_first: "Ajouter un animal d'abord",
      export_pdf_all: "Exporter PDF (tous)",
      export_pdf_one: "Exporter PDF",
      exporting: "Génération…",
      pdf_generated: "PDF généré",
      export_error: "Export impossible",
      types: { vaccine: "Vaccin", treatment: "Traitement", checkup: "Visite vétérinaire", disease: "Maladie", other: "Autre" },
      all_types: "Tous types",
      all_animals: "Tous animaux",
      search_placeholder: "Rechercher (titre, produit, vétérinaire, tag…)",
      reminder_overdue: "En retard",
      reminder_upcoming: "À venir — 30 prochains jours",
      reminder_more: "+ {n} autres…",
      th_date: "Date",
      th_animal: "Animal",
      th_type: "Type",
      th_title: "Intitulé / Produit",
      th_vet: "Vétérinaire",
      th_next: "Prochain rappel",
      th_cost: "Coût",
      stat_total: "Total",
      empty_first_with_animals: "Aucun enregistrement. Cliquez sur « Nouvel enregistrement » pour démarrer.",
      empty_no_animals: "Ajoutez d'abord un animal dans le registre, puis créez son carnet sanitaire ici.",
      empty_search: "Aucun résultat avec ces filtres.",
      animal_deleted: "supprimé",
      animal_label: "Animal*",
      animal_select_ph: "Sélectionner un animal",
      type_label: "Type*",
      date_label: "Date*",
      title_label: "Intitulé*",
      title_ph: "Ex: Vaccin FMD, déparasitage, contrôle gestation…",
      product_label: "Produit / Médicament",
      product_ph: "Ivermectine, Aftovax…",
      dose_label: "Dose / Quantité",
      vet_label: "Vétérinaire",
      vet_ph: "Dr. Ben Salah",
      cost_label: "Coût (TND)",
      next_label: "Prochain rappel",
      next_hint: "S'affichera automatiquement dans les rappels à venir.",
      notes_label: "Notes",
      notes_ph: "Observations, effet secondaire…",
      delete_title: "Supprimer cet enregistrement ?",
      delete_body: "« {title} » du {date}. Cette action est irréversible.",
      saved_toast: "Enregistrement mis à jour",
      created_toast: "Enregistrement ajouté",
      deleted_toast: "Enregistrement supprimé",
      animal_required: "Sélectionnez un animal.",
    },
    pay: {
      back: "Retour à mon espace",
      overline: "Activation immédiate",
      title: "Choisissez votre formule.",
      subtitle: "Paiement sécurisé par carte bancaire via Stripe. Activation automatique dès la confirmation.",
      pay_now: "Payer maintenant",
      per_month: "par mois",
      annual: "Annuel (-20%)",
      monthly: "Mensuel",
      footer: "Tarifs en Dinar Tunisien — converti en USD au moment du paiement test Stripe. En production, le paiement TND direct sera disponible via Konnect.",
      verifying: "Vérification du paiement…",
      verifying_sub: "Merci de patienter quelques secondes.",
      paid_title: "Paiement confirmé !",
      paid_desc: "Votre compte est désormais actif. Bienvenue chez Mazra3ti.",
      goto_dashboard: "Accéder à mon espace",
      timeout_title: "Paiement en cours",
      timeout_desc: "Le paiement met plus de temps que prévu à se confirmer. Vous recevrez un message dès l'activation. Vous pouvez aussi actualiser cette page.",
      expired_title: "Session expirée",
      expired_desc: "La session de paiement a expiré. Vous pouvez réessayer.",
      retry: "Réessayer",
      save_12: "Économisez 12 DT",
      save_36: "Économisez 36 DT",
      essentiel_feats: ["Jusqu'à 50 têtes", "Carnet sanitaire", "Mobile hors-ligne"],
      pro_feats: ["Animaux illimités", "Reproduction", "Stocks & rations", "Analyses"],
      e_m_sub: "par mois",
      e_a_sub: "par an au lieu de 60 DT",
      p_m_sub: "par mois",
      p_a_sub: "par an au lieu de 180 DT",
    },
    login: {
      side_overline: "Espace partenaire & administration",
      side_title_a: "Vos clients,",
      side_title_b: "votre tableau de bord.",
      side_desc: "Activez, suivez et accompagnez chaque éleveur depuis une seule interface, en français comme en arabe.",
      title: "Connexion",
      subtitle: "Entrez vos identifiants partenaire pour accéder à votre espace.",
      email: "Adresse e-mail",
      email_ph: "vous@exemple.tn",
      password: "Mot de passe",
      submit: "Se connecter",
      submitting: "Connexion…",
      twofa_title: "Vérification 2FA",
      twofa_desc: "Entrez le code à 6 chiffres de votre application d'authentification.",
      twofa_label: "Code de vérification",
      twofa_ph: "123 456 ou code de secours XXXX-XXXX",
      twofa_hint: "Vous pouvez aussi entrer un code de récupération à usage unique.",
      twofa_verify: "Vérifier le code",
      twofa_verifying: "Vérification…",
      twofa_back: "← Revenir à l'étape précédente",
    },
    signup: {
      side_overline: "Devenez éleveur connecté",
      side_title_a: "Créez votre compte",
      side_title_b: "en 30 secondes.",
      side_desc: "Accès immédiat — sans validation. Profitez d'1 mois d'essai gratuit pour découvrir tous les modules.",
      perks: ["Activation instantanée, sans attente", "1 mois d'essai gratuit, sans engagement", "Réduction -20% en cas de paiement annuel", "Support direct par WhatsApp"],
      title: "Inscription éleveur",
      subtitle: "Votre compte est activé instantanément avec 1 mois d'essai gratuit.",
      full_name: "Nom complet",
      email: "Adresse e-mail",
      password: "Mot de passe",
      farm: "Filière",
      plan: "Plan",
      billing: "Fréquence",
      submit: "Créer mon compte",
      submitting: "Création…",
      have_account: "Vous avez déjà un compte ?",
      login_link: "Connectez-vous",
      success_title: "Bienvenue chez Mazra3ti !",
      success_desc: "Votre compte est activé. Vous bénéficiez d'1 mois d'essai gratuit. Accédez immédiatement à votre tableau de bord.",
      success_cta_dashboard: "Accéder à mon tableau de bord",
      already_have: "J'ai déjà un compte — me connecter",
      consent_label_fr: "J'accepte les",
      consent_cgu: "Conditions Générales d'Utilisation",
      consent_and: "et la",
      consent_privacy: "Politique de Confidentialité",
      consent_must_accept: "Vous devez accepter les CGU pour créer votre compte.",
      rule_length: "Min 10 caractères",
      rule_upper: "1 majuscule",
      rule_digit: "1 chiffre",
      rule_special: "1 caractère spécial",
      plan_e: "Essentiel — 5 DT / mois (1 mois d'essai offert)",
      plan_p: "Pro — 15 DT / mois (1 mois d'essai offert)",
      plan_d: "Domaine — sur devis",
    },
    change_pw: {
      title_forced: "Sécurisez votre compte",
      title_normal: "Changer mon mot de passe",
      subtitle_forced: "Pour des raisons de sécurité, vous devez définir un nouveau mot de passe avant d'accéder à votre espace.",
      subtitle_normal: "Choisissez un nouveau mot de passe fort.",
      current: "Mot de passe actuel",
      new_pw: "Nouveau mot de passe",
      confirm: "Confirmer le nouveau mot de passe",
      submit: "Mettre à jour mon mot de passe",
      submitting: "Mise à jour…",
      mismatch: "Les deux mots de passe ne correspondent pas.",
      toast_updated: "Mot de passe mis à jour",
    },
    whatsapp: {
      cta_said: "Discuter avec Haithem sur WhatsApp",
      hello_said: "Bonjour Haithem 👋",
      signup_msg: "Bonjour Haithem, je viens de m'inscrire sur Mazra3ti ({name}, {farm}). Pouvez-vous me guider pour démarrer ?",
      pending_msg: "Bonjour Haithem, mon essai gratuit est terminé sur Mazra3ti. Pouvez-vous m'aider à activer mon abonnement ?",
    },
    trial: {
      badge: "Essai gratuit",
      banner_title: "Essai gratuit en cours",
      days_left_one: "Il vous reste {n} jour",
      days_left_many: "Il vous reste {n} jours",
      ends_today: "Votre essai se termine aujourd'hui",
      banner_sub: "Profitez de toutes les fonctionnalités. Activez votre abonnement quand vous voulez.",
      activate_btn: "Activer mon abonnement",
      perks_title: "Inclus dans votre essai",
      perks: ["Tous les modules débloqués", "Vos données conservées", "Support WhatsApp inclus"],
    },
    activity: {
      th_last_login: "Dernière connexion",
      never: "Jamais",
      today: "Aujourd'hui",
      yesterday: "Hier",
      days_ago: "Il y a {n}j",
      inactive: "Inactif",
      send_reminder: "Envoyer une relance",
      reminder_sent_title: "Relance envoyée",
      reminder_sent: "Relance envoyée à {name}.",
      reminder_sent_mocked: "Relance enregistrée (mode test, sans Twilio).",
    },
    expert: {
      title: "Conseils techniques d'élevage",
      subtitle: "Posez vos questions à Seifeddine, notre ingénieur agronome (conduite d'élevage & nutrition).",
      whatsapp_card: "Question écrite (WhatsApp)",
      whatsapp_desc: "Conduite, nutrition, reproduction — réponse sous 24h.",
      call_card: "Demander un rappel",
      call_desc: "Seifeddine vous rappelle pour échanger.",
      remaining: "Il vous reste {n}/{max} ce mois-ci",
      modal_title_whatsapp: "Poser une question à Seifeddine",
      modal_title_call: "Demander un rappel",
      subject_label: "Sujet",
      subject_ph: "Ex : Quelle ration pour mes brebis gestantes ?",
      message_label: "Votre question",
      message_ph: "Décrivez votre situation : âge des animaux, alimentation actuelle, contexte…",
      submit: "Envoyer",
      submitting: "Envoi…",
      success_title: "Demande envoyée ✓",
      success_desc: "Seifeddine vous répondra par WhatsApp dans les 24h.",
      locked_title: "Service Premium — Plan Pro",
      locked_desc_trial: "Pendant l'essai gratuit ce service n'est pas disponible. Activez votre abonnement Pro (15 DT/mois) pour y accéder.",
      locked_desc_plan: "Passez au plan Pro (15 DT/mois) pour bénéficier de 5 questions WhatsApp + 5 appels/mois avec Seifeddine.",
      upgrade_btn: "Activer le plan Pro",
      quota_exhausted: "Quota mensuel atteint — renouvellement le 1er du mois prochain.",
      legal_disclaimer: "⚠️ Important : Seifeddine est ingénieur agronome, pas vétérinaire. Il ne peut pas prescrire de médicaments ni établir de diagnostic médical. Pour toute pathologie, blessure ou prescription, consultez impérativement un vétérinaire diplômé.",
    },
    repro: {
      overline: "Reproduction du troupeau",
      title: "Reproduction",
      subtitle: "Saillies, inséminations, gestations, mises bas et généalogie — tout votre cycle reproducteur en un coup d'œil.",
      new_event: "Nouvel événement",
      add_title: "Nouvel événement de reproduction",
      edit_title: "Modifier l'événement",
      add_animal_first: "Ajoutez d'abord une femelle",
      mod_title: "Reproduction",
      mod_desc: "Suivez chaleurs, saillies/IA, gestations et mises bas. Calcul auto des dates de mise bas.",
      no_females_warning: "Aucune femelle dans votre troupeau. Ajoutez une femelle dans le registre pour démarrer.",
      types: {
        breeding: "Saillie / IA",
        pregnancy_check: "Diagnostic gestation",
        birth: "Mise bas",
        abortion: "Avortement",
        weaning: "Sevrage",
      },
      methods: { natural: "Saillie naturelle", ai: "Insémination artificielle (IA)" },
      outcomes: {
        pending: "En attente diagnostic",
        confirmed_pregnant: "Gestante confirmée",
        not_pregnant: "Non gestante",
        birth_done: "Mise bas effectuée",
        aborted: "Avortement",
      },
      all_types: "Tous types",
      all_females: "Toutes femelles",
      stat_total: "Total",
      stat_active_pregnancies: "Gestations en cours",
      reminder_overdue: "Mises bas en retard",
      reminder_upcoming: "Mises bas à venir — 30 jours",
      reminder_more: "+ {n} autres…",
      search_placeholder: "Rechercher (tag, étalon, vétérinaire…)",
      th_date: "Date",
      th_female: "Femelle",
      th_type: "Type",
      th_method: "Méthode / Détails",
      th_partner: "Étalon / Donneur",
      th_outcome: "Statut",
      th_due: "Mise bas prévue",
      th_offspring: "Petits",
      empty_first_with_females: "Aucun événement. Cliquez sur « Nouvel événement » pour démarrer.",
      empty_search: "Aucun résultat avec ces filtres.",
      female_label: "Femelle*",
      female_select_ph: "Sélectionner une femelle",
      female_only: "Seules les femelles sont listées.",
      type_label: "Type d'événement*",
      date_label: "Date*",
      method_label: "Méthode (saillie/IA)",
      partner_tag_label: "Étalon / Donneur (tag ou nom)",
      partner_ph: "TN-M015, Bull-Said-Holstein…",
      expected_birth_label: "Mise bas prévue",
      expected_birth_hint: "Calculée automatiquement selon l'espèce. Modifiable.",
      outcome_label: "Statut",
      offspring_count_label: "Nombre de petits",
      offspring_alive_label: "Petits vivants",
      vet_label: "Vétérinaire",
      vet_ph: "Dr. Ben Salah",
      cost_label: "Coût (TND)",
      notes_label: "Notes",
      notes_ph: "Difficulté, complications, observations…",
      delete_title: "Supprimer cet événement ?",
      delete_body: "Événement « {type} » du {date}. Cette action est irréversible.",
      saved_toast: "Événement mis à jour",
      created_toast: "Événement enregistré",
      deleted_toast: "Événement supprimé",
      female_required: "Sélectionnez une femelle.",
    },
    calendar: {
      overline: "Planning du mois",
      title: "Calendrier",
      subtitle: "Tous vos rappels sanitaires et mises bas à venir, sur un coup d'œil.",
      mod_title: "Calendrier",
      mod_desc: "Vue mensuelle de tous les événements à venir (vaccins, traitements, mises bas).",
      prev: "Mois précédent",
      next: "Mois suivant",
      today: "Aujourd'hui",
      no_events: "Aucun événement ce jour.",
      legend_health: "Carnet sanitaire",
      legend_birth: "Mise bas",
      days_short: ["L", "M", "M", "J", "V", "S", "D"],
      months: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
      open_module: "Ouvrir",
      today_label: "Aujourd'hui",
    },
    nutrition: {
      overline: "Alimentation du troupeau",
      title: "Nutrition",
      subtitle: "Suivez vos stocks (silos, fourrage, concentrés) et le coût d'alimentation par animal.",
      mod_title: "Alimentation",
      mod_desc: "Rations, stocks et coût par animal. Décrémentation auto du silo.",
      tab_stocks: "Stocks",
      tab_feedings: "Rationnement",
      tab_calculator: "Calculateur de rations",
      new_stock: "Nouveau stock",
      new_feeding: "Nouvelle ration",
      add_stock_title: "Ajouter un stock",
      add_feeding_title: "Distribuer une ration",
      types: { concentre: "Concentré", fourrage: "Fourrage vert", foin: "Foin", paille: "Paille", ensilage: "Ensilage", mineraux: "Minéraux / CMV", autre: "Autre" },
      stat_total_stocks: "Stocks enregistrés",
      stat_stock_value: "Valeur des stocks",
      stat_total_feedings: "Rations distribuées",
      stat_feed_cost: "Coût alimentation",
      stat_total_quantity: "Quantité distribuée",
      stocks_empty: "Aucun stock. Cliquez sur « Nouveau stock » pour démarrer.",
      feedings_empty: "Aucune ration distribuée pour l'instant.",
      add_animal_first: "Ajoutez un animal d'abord",
      th_name: "Nom",
      th_type: "Type",
      th_quantity: "Quantité",
      th_cost_per_kg: "Coût/kg",
      th_value: "Valeur",
      th_supplier: "Fournisseur",
      th_animal: "Animal",
      th_feed: "Aliment",
      th_date: "Date",
      th_qty: "Qté (kg)",
      th_cost: "Coût",
      name_label: "Nom du stock*",
      name_ph: "Concentré laitier, Foin Sulla…",
      type_label: "Type*",
      quantity_label: "Quantité disponible (kg)*",
      cost_per_kg_label: "Coût par kg (TND)*",
      supplier_label: "Fournisseur",
      supplier_ph: "Coopérative El Fath…",
      animal_label: "Animal*",
      animal_select_ph: "Sélectionner un animal",
      feed_item_label: "Stock utilisé",
      feed_item_optional: "(ou nom libre ci-dessous)",
      feed_item_none: "— Sans stock —",
      feed_name_label: "Nom libre (si pas de stock)",
      feed_name_ph: "Pâturage extérieur, achat ponctuel…",
      date_label: "Date*",
      qty_label: "Quantité distribuée (kg)*",
      cost_label_override: "Coût (TND, optionnel)",
      cost_hint: "Si laissé vide, calculé automatiquement à partir du stock.",
      notes_label: "Notes",
      notes_ph: "Observations, repas, lot…",
      saved_toast: "Enregistré",
      deleted_toast: "Supprimé",
      delete_stock_title: "Supprimer ce stock ?",
      delete_feeding_title: "Supprimer cette ration ?",
      search_placeholder: "Rechercher (animal, aliment, fournisseur)…",
    },
    husbandry: {
      tabs: { weighing: "Pesées", famacha: "FAMACHA", hoof_trim: "Onglons", shearing: "Tonte", antiparasitic: "Antiparasitaire", lineage: "Filiation" },
      common: { date: "Date", notes: "Notes (optionnel)", notes_ph: "État général, traitement appliqué…", add: "Ajouter", save: "Enregistrer", delete_confirm: "Supprimer cet événement ?", deleted: "Supprimé", saved: "Enregistré ✓", error: "Erreur", no_events: "Aucun événement enregistré.", how_to: "Comment faire", info_btn: "Pourquoi & outils", guide_btn: "Guide complet", guide_period: "Guide & période" },
      weighing: {
        title: "Pesées",
        stat_count: "Pesées", stat_current: "Poids actuel", stat_delta: "Évolution",
        stat_gmq: "GMQ moyen",
        gmq_unit: "g/jour", gmq_short: "g/j",
        gmq_chart_label: "GMQ entre chaque pesée (g/jour)",
        form_weight: "Poids (kg)", form_weight_ph: "Ex: 48.5",
        weight_required: "Poids requis",
        chart_label: "Courbe d'évolution",
        add_btn: "Ajouter",
      },
      famacha: {
        banner_title: "Score FAMACHA — détection du parasitisme",
        banner_desc: "Examinez la couleur de la conjonctive de l'œil pour détecter l'anémie causée par les parasites internes (haemonchus).",
        choose_label: "Choisissez le score correspondant à la couleur observée",
        action_label: "Action recommandée :",
        save_btn: "Enregistrer le score",
        choose_required: "Choisissez un score",
        levels: [
          { label: "Rouge vif", verdict: "Optimal", action: "Aucun traitement. Animal sain." },
          { label: "Rouge-rose", verdict: "Bon", action: "Aucun traitement nécessaire." },
          { label: "Rose", verdict: "À surveiller", action: "Surveillance accrue. Vermifuger si autres signes (poil terne, diarrhée)." },
          { label: "Rose pâle", verdict: "Anémie modérée", action: "Vermifuger immédiatement. Contacter le vétérinaire si persistant." },
          { label: "Blanc pâle", verdict: "Anémie sévère", action: "🚨 URGENT — Vermifuger + appeler le vétérinaire. Risque vital." },
        ],
        guide_title: "Comment faire un examen FAMACHA",
        why_title: "Pourquoi ?",
        why_desc: "Le système FAMACHA permet de détecter l'anémie causée par l'Haemonchus contortus, parasite gastro-intestinal qui suce le sang. C'est l'outil de référence pour décider si un mouton doit être vermifugé — ce qui limite le gaspillage de produits et la résistance aux antiparasitaires.",
        how_title: "Comment faire ?",
        steps: [
          "Immobilisez l'animal calmement.",
          "Abaissez la paupière inférieure avec le pouce pour exposer la conjonctive.",
          "Comparez la couleur observée à la palette de l'échelle (1=rouge vif → 5=blanc).",
          "Notez le score dans Mazra3ti et appliquez l'action recommandée.",
        ],
        tip: "💡 Faites le test à plusieurs animaux du troupeau pour détecter rapidement les sujets à risque.",
      },
      hoof: {
        banner_title: "Parage des onglons",
        banner_desc: "Tous les 4 à 6 mois pour éviter boiteries, fourbures et infections. Animal calme et propre.",
        add_btn: "Marquer le parage comme fait",
        event_label: "Parage effectué",
        guide_title: "Parage des onglons — Guide complet",
        steps_title: "Étapes",
        tools_title: "Outils nécessaires",
        warning: "⚠️ Ne jamais couper jusqu'à la chair vive (rose). Si saignement : désinfecter immédiatement à l'iode.",
        tools: [
          { name: "Cisaille à onglons", desc: "Outil principal pour couper la corne en excès" },
          { name: "Couteau à parer (renette)", desc: "Pour égaliser et nettoyer la sole" },
          { name: "Lime / rasp", desc: "Pour finir et lisser la corne" },
          { name: "Antiseptique iodé", desc: "Désinfecter après la coupe" },
          { name: "Gants jetables", desc: "Hygiène et protection" },
        ],
        steps: [
          "Immobilisez l'animal (cornadis, tip-stand ou aide).",
          "Nettoyez l'onglon avec une brosse pour voir les contours.",
          "Coupez la corne en excès avec la cisaille (jamais jusqu'à la chair vive).",
          "Égalisez la sole avec le couteau à parer.",
          "Finissez avec la lime pour un aplomb régulier.",
          "Désinfectez si vous avez touché du tissu sensible.",
        ],
      },
      shearing: {
        banner_title: "Tonte",
        banner_desc: "Période idéale : Mai – Juin (avant les chaleurs). Toujours par temps sec.",
        form_wool: "Laine récoltée (kg)", form_wool_ph: "Ex: 2.5",
        form_notes_ph: "Qualité, tondeur…",
        add_btn: "Enregistrer la tonte",
        event_label: "Tonte",
        wool_label: "kg laine",
        guide_title: "Guide de tonte",
        periods_title: "Périodes recommandées",
        tools_title: "Outils",
        steps_title: "Étapes",
        periods: [
          { species: "Ovins (moutons)", period: "Mai – Juin", reason: "Avant les fortes chaleurs, après la fin des nuits froides." },
          { species: "Caprins (chèvres angora)", period: "Mars et Septembre", reason: "Deux tontes/an pour les races à toison." },
        ],
        tools: ["Tondeuse électrique professionnelle", "Peignes et couteaux de rechange", "Huile pour tondeuse", "Pinces hémostatiques (en cas de coupure)", "Iodine pour désinfecter"],
        steps: [
          "Tondez par temps sec — la laine humide est invendable.",
          "Animal calme, posé sur l'arrière-train (position du tondeur).",
          "Commencez par le ventre, remontez vers le dos en passes longues.",
          "Restez près de la peau sans la blesser.",
          "Triez la laine immédiatement : suint, jarre, fibre noble.",
        ],
      },
      antiparasitic: {
        banner_title: "Bain antiparasitaire",
        banner_desc: "2 à 3 fois/an. Renouveler 14 jours après pour casser le cycle des parasites.",
        form_product: "Produit utilisé", form_product_ph: "Ex: Amitraz, Deltamethrine…",
        form_notes_ph: "Dose, observations…",
        add_btn: "Enregistrer",
        default_event: "Bain antiparasitaire",
        guide_title: "Bain antiparasitaire — Pourquoi & Comment",
        why_title: "Pourquoi le faire ?",
        why_desc: "Lutte contre la gale (psoroptose), les poux (mallophages) et les tiques. Indispensable pour éviter perte de poids, démangeaisons, dermatites graves.",
        frequency_title: "Fréquence recommandée",
        frequency_desc: "2 à 3 fois par an : printemps (avril-mai), été (juillet), automne (octobre).",
        tools_title: "Outils",
        steps_title: "Étapes",
        warning: "⚠️ Les produits acaricides sont toxiques. Consultez votre vétérinaire pour le bon produit et le respect des délais d'attente avant abattage.",
        tools: ["Bain (pédiluve long) ou pulvérisateur", "Produit acaricide/insecticide (Amitraz, Deltamethrine — sur ordonnance vétérinaire)", "Combinaison + gants + masque pour l'éleveur", "Eau propre pour rincer si besoin"],
        steps: [
          "Préparez la solution selon la notice (dosage strict).",
          "Faites passer l'animal dans le bain, immergez la tête 2-3 secondes.",
          "Durée d'immersion : 30 à 60 secondes selon le produit.",
          "Renouvelez 14 jours plus tard pour casser le cycle des parasites.",
          "Ne pas traiter les femelles gestantes les 15 derniers jours.",
        ],
      },
      lineage: {
        banner_title: "Arbre généalogique (3 générations)",
        banner_desc: "Affiche les parents et grands-parents enregistrés.",
        grand_mother: "Grand-mère ♀", grand_father: "Grand-père ♂",
        mother: "Mère ♀", father: "Père ♂",
        self: "Lui/Elle", child: "Enfant",
        empty_node: "Non renseigné",
        children_label: "Descendance",
      },
    },
    analytics: {
      overline: "Vue d'ensemble",
      title: "Analyses & rentabilité",
      subtitle: "Vos KPI clés pour piloter votre exploitation : troupeau, santé, reproduction et coûts.",
      mod_title: "Analyses",
      mod_desc: "Tableau de bord KPI : effectif, coûts, gestations, rentabilité. Export PDF complet.",
      export_pdf: "Exporter le rapport PDF",
      exporting: "Génération…",
      section_herd: "Troupeau",
      section_health: "Santé",
      section_repro: "Reproduction",
      section_nutrition: "Nutrition",
      section_costs: "Coûts",
      animals_total: "Animaux enregistrés",
      animals_active: "Actifs",
      animals_sold: "Vendus",
      animals_dead: "Décédés",
      health_total: "Actes sanitaires",
      health_cost: "Coût santé",
      repro_events: "Événements",
      repro_active_preg: "Gestations en cours",
      repro_births: "Mises bas",
      repro_offspring: "Petits vivants nés",
      nutri_stocks: "Stocks",
      nutri_value: "Valeur stocks",
      nutri_feedings: "Rations",
      nutri_cost: "Coût alimentation",
      cost_total: "Coût total opérationnel",
      cost_per_animal: "Coût moyen / animal actif",
    },
    tour: {
      start_btn: "Visite guidée",
      skip: "Passer",
      previous: "Précédent",
      next: "Suivant",
      finish: "C'est parti !",
      step_label: "Étape {n} sur {total}",
      welcome_title: "Bienvenue chez Mazra3ti 🌾",
      welcome_desc: "En 60 secondes, découvrez les 6 modules qui vont transformer la gestion de votre élevage.",
      steps: [
        { title: "Registre des animaux", desc: "Le point de départ. Enregistrez chaque tête : tag, espèce, race, sexe, âge, poids. C'est la base de tout." },
        { title: "Carnet sanitaire", desc: "Saisissez vaccins, traitements et visites vétérinaires. Les rappels à venir s'affichent automatiquement. Exportez en PDF pour les contrôles." },
        { title: "Reproduction", desc: "Suivez saillies, IA, gestations et mises bas. La date de mise bas est calculée automatiquement selon l'espèce." },
        { title: "Calendrier unifié", desc: "Tous vos rappels sanitaires et mises bas en une vue mensuelle. Plus jamais d'oubli." },
        { title: "Alimentation", desc: "Gérez vos silos et distribuez des rations. Le coût par animal est calculé en temps réel, le stock se met à jour seul." },
        { title: "Analyses & rentabilité", desc: "Tous vos KPI au même endroit : effectif, coûts, gestations, rentabilité. Rapport PDF exportable." },
      ],
    },
  },
  pwa: {
    install_title: "Installer Mazra3ti",
    install_desc: "Ajoutez l'application à votre écran d'accueil pour un accès rapide, même hors-ligne.",
    install_cta: "Installer",
    install_dismiss: "Plus tard",
    ios_hint: "Sur iPhone : appuyez sur Partager puis « Sur l'écran d'accueil ».",
    update_title: "Nouvelle version disponible",
    update_desc: "Une mise à jour de Mazra3ti est prête à être installée.",
    update_cta: "Mettre à jour",
  },
};

const ar = {
  dir: "rtl",
  brand: {
    name: "Mazra3ti",
    tagline: "أدر مزرعتك ببساطة.",
  },
  nav: {
    features: "الميزات",
    pricing: "الأسعار",
    faq: "الأسئلة الشائعة",
    contact: "اتصل بنا",
    demo: "طلب عرض توضيحي",
    waitlist: "قائمة الانتظار",
    langSwitch: "Français",
    langSwitchAria: "التبديل إلى الفرنسية",
  },
  hero: {
    overline: "تطبيق المربّين",
    headline_a: "أدر مزرعتك",
    headline_b: "ببساطة.",
    subheadline:
      "تابع صحة وتكاثر وتغذية قطيعك في الوقت الفعلي. الأداة الأساسية لعصرنة مزرعتك، من الإسطبل إلى المرعى.",
    cta_primary: "أنشئ حسابي — شهر مجاني",
    cta_login: "فضاء العميل — تسجيل الدخول",
    cta_dashboard: "فضائي في Mazra3ti",
    email_placeholder: "بريدك الإلكتروني",
    submit: "انضم إلى القائمة",
    submitting: "جاري التسجيل...",
    trust_hosting: "بيانات مشفّرة وآمنة",
    trust_offline: "يعمل دون اتصال",
    trust_trial: "تجربة مجانية، دون التزام",
    stat_card_label: "رأس مواشي متابعة",
    stat_card_sub: "+12 حيوانًا جديدًا هذا الشهر",
    stats: [
      ["+1 200", "مربٍّ في الانتظار"],
      ["24", "ولاية مغطاة"],
      ["94٪", "رضا التجربة"],
      ["0", "وثيقة ضائعة"],
    ],
  },
  features: {
    overline: "الميزات",
    headline_a: "كل ما تحتاجه،",
    headline_b: "في مكان واحد.",
    description:
      "منصة مصممة مع ومن أجل المربّين. مصممة لتلائم العمل الميداني، ومصممة لتوفير الوقت كل يوم.",
    items: {
      sante: {
        title: "متابعة صحية ذكية",
        description:
          "دفتر صحي رقمي، تتبع كامل، تنبيهات تلقائية للتطعيمات وتذكيرات مخصصة لكل حيوان.",
      },
      reproduction: {
        title: "التكاثر",
        description: "توقع فترات الشبق ومتابعة الحمل.",
      },
      alimentation: {
        title: "التغذية",
        description: "تحسين الحصص وإدارة الصوامع مع حسابات غذائية.",
      },
      mobile: {
        title: "محمول 100٪",
        description: "متاح دون اتصال، مباشرة من المرعى.",
      },
      analyses: {
        title: "تحليلات وربحية",
        description: "لوحات معلومات واضحة لإدارة مزرعتك.",
      },
    },
  },
  trainer: {
    overline: "المؤسس المرافق",
    headline_a: "بمرافقة",
    headline_b: "هيثم،",
    headline_c: "مستشارك في Mazra3ti.",
    school: "Mazra3ti",
    school_full: "Mazra3ti — منصة مخصصة للمربّين",
    description:
      "هيثم السكال، مؤسس Mazra3ti، أرافق شخصيًا كل مربٍّ في استعمال المنصّة. متاح مباشرة عبر واتساب لأي سؤال تقني أو تجاري.",
    perks: [
      "تفعيل فوري لحسابك",
      "شهر تجربة مجاني دون التزام",
      "مرافقة شخصية من المؤسس",
      "دعم تقني مباشر عبر واتساب",
    ],
    education_title: "خرّيج مركز تكوين الخبراء",
    education_school: "A.C.A Consult — صفاقس",
    competencies: [
      "تربية الأغنام",
      "تقليم شجرة الزيتون",
      "تركيب وتصنيع الأعلاف المركّزة",
      "تقنيات معاصر الزيتون",
      "التصدير الدولي",
    ],
    cta_whatsapp: "تواصل مع هيثم عبر واتساب",
    cta_call: "اتصل",
    badge: "المؤسس",
    team_overline: "فريق Mazra3ti",
    team_title: "فريق في خدمتك",
    team_desc: "إلى جانب الدعم التقني، تستفيد من خبرة مهندس متخصص لكل أسئلتك حول التربية.",
    team_disclaimer: "النصائح المقدمة من فريقنا تخص فقط إدارة المزرعة والتغذية والممارسات الجيدة. لأي وصفة دوائية أو تشخيص بيطري، استشر طبيبًا بيطريًا مرخّصًا.",
    team_members: [
      {
        name: "سيف الدين بن رجب",
        role: "مهندس فلاحي وحيواني",
        bio: "يرافقكم في إدارة القطيع: التغذية، الحصص الغذائية، التكاثر، الإدارة التقنية وأفضل ممارسات التربية. لأي سؤال طبي، يحيلكم إلى طبيب بيطري.",
        initials: "SB",
        photo: "/seifeddine-avatar.jpg",
        education: ["الكاف · تونس", "إيطاليا · هضم الحيوان"],
        scope_in: ["التغذية والحصص", "التكاثر التقني", "إدارة القطيع", "الممارسات الجيدة"],
        scope_out: ["وصف الأدوية", "التشخيص البيطري", "الأعمال الطبية"],
      },
    ],
  },
  pricing: {
    overline: "الأسعار",
    headline: "أسعار مناسبة لمزرعتك.",
    description: "دون التزام، قابل للإلغاء في أي وقت. شهر مجاني لكل مزرعة جديدة.",
    unit: "/ شهر",
    popular: "الأكثر شعبية",
    on_quote: "حسب الطلب",
    plans: {
      essential: {
        name: "أساسي",
        price: "5 د.ت",
        badge: "الشهر الأول مجاني",
        description: "للمزارع الصغيرة التي تبدأ الرقمنة.",
        features: [
          "حتى 50 رأس",
          "دفتر صحي رقمي",
          "تطبيق محمول بدون اتصال",
          "دعم عبر البريد الإلكتروني",
        ],
        note: "وحدات إضافية متاحة كخيار مدفوع.",
        cta: "تجربة مجانية",
      },
      pro: {
        name: "محترف",
        price: "15 د.ت",
        description: "للمربّين الطموحين والمتنامين.",
        features: [
          "حيوانات غير محدودة",
          "تنبيهات التكاثر والشبق",
          "إدارة المخزون والحصص",
          "تحليل الربحية",
          "5 أسئلة + 5 مكالمات/شهر مع سيف الدين (نصائح تقنية للتربية)",
          "دعم ذو أولوية",
        ],
        cta: "ابدأ الآن",
      },
      domain: {
        name: "ضيعة",
        description: "للهياكل الكبرى والتعاضديات.",
        features: [
          "مواقع متعددة ومستخدمون متعددون",
          "واجهة برمجية وتكاملات محاسبية",
          "نصائح تقنية غير محدودة مع سيف الدين (تربية، تغذية)",
          "مرافقة مخصصة",
          "اتفاقية مستوى الخدمة مضمونة",
        ],
        cta: "اتصل بفريق المبيعات",
      },
    },
  },
  faq: {
    overline: "الأسئلة الشائعة",
    headline: "أسئلة متكررة",
    hint_a: "سؤال غير مدرج هنا؟",
    hint_link: "اكتب لنا",
    items: [
      {
        q: "هل يعمل التطبيق بدون إنترنت؟",
        a: "نعم، يتوفر Mazra3ti على وضع كامل بدون اتصال. يمكنك تسجيل عمليات الوزن أو العلاج في عمق المرعى، وتُزامن بياناتك تلقائيًا فور استعادة الاتصال.",
      },
      {
        q: "هل بياناتي آمنة وخاصة؟",
        a: "بالطبع. بياناتك مشفرة من طرف إلى طرف (HTTPS + تشفير كلمات المرور)، ومستضافة على بنية تحتية سحابية آمنة، وهي ملكك بنسبة 100٪. يمكنك تصديرها أو حذفها في أي وقت.",
      },
      {
        q: "هل يمكنني تصدير دفتري الصحي لطبيبي البيطري؟",
        a: "نعم، يمكنك تصدير سجلاتك بصيغة PDF أو Excel بنقرة واحدة. كما يمكنك دعوة طبيبك البيطري في وضع القراءة فقط لاستشارة بطاقات الصحة مباشرة.",
      },
      {
        q: "كم من الوقت يلزم للانطلاق؟",
        a: "معظم المربّين جاهزون للعمل في أقل من ساعة. نقدم استيرادًا مجانيًا لبياناتك الموجودة (Excel، ورق، برنامج آخر) ومرافقة هاتفية.",
      },
      {
        q: "ما هي أنواع التربية المدعومة؟",
        a: "أبقار (حليب ولحم)، أغنام، ماعز، أرانب، خيول. إذا لم يكن قطاعك مدرجًا، اتصل بنا: نُكيّف المنصة بانتظام مع احتياجات الميدان.",
      },
      {
        q: "هل هناك التزام بمدة معينة؟",
        a: "لا التزام. يمكنك إلغاء اشتراكك بنقرة واحدة، وتبقى بياناتك قابلة للتصدير لمدة 12 شهرًا بعد الإلغاء.",
      },
    ],
  },
  contact: {
    overline: "اتصل بنا",
    headline: "هل أنت مستعد لعصرنة مزرعتك؟",
    description:
      "تفعيل فوري: أنشئ حسابك واستمتع بشهر مجاني، دون التزام. عندك سؤال؟ اتّصل بنا مباشرة عبر واتساب.",
    signup_cta: "أنشئ حسابي — شهر مجاني",
    login_cta: "عندي حساب فعلًا",
    waitlist_placeholder: "بريدك الإلكتروني",
    waitlist_submit: "أسجّل",
    email: "contact@mazra3ti.tn",
    phone: "+216 71 000 000",
    address: "تونس — تونس",
    form_title: "طلب عرض توضيحي",
    form_subtitle: "صف لنا مزرعتك، وسنعاود الاتصال بك سريعًا.",
    labels: {
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      farm_type: "نوع التربية",
      message: "رسالتك",
    },
    placeholders: {
      name: "محمد بن علي",
      email: "mohamed@ferme.tn",
      farm_type: "اختر قطاعك",
      message: "أخبرنا عن مزرعتك واحتياجاتك…",
    },
    farm_options: {
      Bovins: "أبقار",
      Ovins: "أغنام",
      Caprins: "ماعز",
      Lapins: "أرانب",
      Autre: "أخرى",
    },
    disclaimer:
      "بإرسال هذا النموذج، توافق على أن يتواصل معك فريقنا.",
    submit: "إرسال الرسالة",
    submitting: "جاري الإرسال…",
  },
  footer: {
    tagline:
      "المنصة الحديثة لإدارة مزرعتك، من الدفتر الصحي إلى ربحية استغلالك.",
    col_product: "المنتج",
    col_company: "الشركة",
    rights: "جميع الحقوق محفوظة",
    legal: "إشعارات قانونية",
    privacy: "الخصوصية",
    cgv: "الشروط العامة",
  },
  toasts: {
    wait_success_title: "تم التسجيل بنجاح!",
    wait_success_desc: "أنت الآن على قائمة انتظار Mazra3ti.",
    wait_success_desc_short: "سنخبرك بكل جديد عند الإطلاق.",
    wait_dup_title: "مسجّل مسبقًا",
    wait_dup_desc: "هذا البريد الإلكتروني مدرج بالفعل في قائمة الانتظار.",
    error_title: "خطأ",
    error_desc: "تعذّر تسجيلك حاليًا.",
    missing_title: "حقول مفقودة",
    missing_desc: "يرجى ملء جميع الحقول.",
    contact_success_title: "تم إرسال الرسالة!",
    contact_success_desc: "سيرد عليك فريقنا خلال 24 ساعة عمل.",
    contact_error_desc: "تعذّر إرسال الرسالة حاليًا.",
  },
  app: {
    common: {
      loading: "جاري التحميل…",
      save: "حفظ",
      saving: "جاري الحفظ…",
      update: "تحديث",
      updating: "جاري التحديث…",
      cancel: "إلغاء",
      edit: "تعديل",
      delete: "حذف",
      deleting: "جاري الحذف…",
      create: "إنشاء",
      creating: "جاري الإنشاء…",
      logout: "تسجيل الخروج",
      back: "رجوع",
      back_home: "العودة إلى الرئيسية",
      actions: "إجراءات",
      search: "بحث",
      filter: "تصفية",
      yes: "نعم",
      no: "لا",
      view_all: "عرض الكل",
      lang_toggle: "Français",
      lang_toggle_aria: "التبديل إلى الفرنسية",
      irreversible: "هذا الإجراء لا رجعة فيه.",
      no_results: "لا توجد نتائج.",
      none: "—",
    },
    role: { owner: "المالك", trainer: "المدرّب", client: "المربّي" },
    status: { active: "نشط", pending: "في الانتظار", suspended: "موقوف", sold: "مباع", dead: "نافق" },
    shell: {
      overview: "نظرة عامة",
      clients: "الزبائن",
      trainers: "المدرّبون",
      audit: "سجل التدقيق",
      security: "الأمان (2FA)",
      landing: "عرض الصفحة الرئيسية",
      expert_inbox: "صندوق الخبير",
    },
    owner: {
      overline: "فضاء المالك",
      welcome: "مرحبًا",
      subtitle: "نظرة عامة على نشاط Mazra3ti الخاص بك.",
      view_all_clients: "كل الزبائن",
      stat_total: "إجمالي الزبائن",
      stat_active: "النشطون",
      stat_pending: "في الانتظار",
      stat_suspended: "الموقوفون",
      mrr: "الإيرادات الشهرية المتكررة",
      per_month: "/ شهر",
      annual_estimate: "≈ {n} د.ت / سنة عند الاستقرار",
      commission_said: "عمولة المستشار (50%)",
      net_owner: "صافي لك",
      plan_breakdown: "التوزيع حسب الباقة",
      recent_title: "آخر 5 زبائن",
      no_clients: "لا يوجد زبائن حاليًا.",
    },
    trainer: {
      overline: "فضاء المدرّب",
      hello: "أهلا",
      subtitle: "زبائنك، تفعيلهم وعمولتك، في لمحة واحدة.",
      new_client: "زبون جديد",
      signup_banner_count_one: "{n} حساب جديد في انتظار التفعيل",
      signup_banner_count_many: "{n} حسابات جديدة في انتظار التفعيل",
      signup_banner_sub: "سجّل مربٍّ عبر الموقع. فعّل حسابه بنقرة واحدة.",
      stat_total: "إجمالي الزبائن",
      mrr_label: "الإيرادات الشهرية المتكررة",
      mrr_sub: "على الزبائن النشطين الظاهرين",
      commission_label: "عمولتك (50%)",
      commission_sub: "تقديرية لهذا الشهر",
      manage_clients: "إدارة زبائني",
      recent_title: "آخر الحسابات المنشأة",
      no_clients: "لا يوجد زبائن. أنشئ أول حساب.",
    },
    client: {
      hello: "أهلا",
      subtitle: "فضاؤك على Mazra3ti لإدارة مزرعتك.",
      pending_title: "فعّل حسابك",
      pending_desc: "حسابك في وضع الانتظار. يمكنك تفعيله فورًا عبر الدفع الآمن على الإنترنت، أو التواصل معنا عبر واتساب لإتمام العملية.",
      pay_now: "ادفع الآن عبر الإنترنت",
      suspended_title: "انتهت فترة التجربة المجانية",
      suspended_desc: "لمواصلة استعمال Mazra3ti، فعّل اشتراكك عبر الدفع الآمن. ستحتفظ بكل بياناتك.",
      active_status: "حساب نشط",
      active_plan: "باقة {plan}، دفع {cycle}",
      monthly: "شهري",
      annual: "سنوي",
      modules_intro: "أهلًا بك في Mazra3ti. هذه الوحدات المتاحة اليوم.",
      mod_animals_title: "سجل الحيوانات",
      mod_animals_desc: "أضف وتابع كل رأس مواش: التعريف، النوع، السلالة، الجنس، العمر، الوزن.",
      mod_health_title: "الدفتر الصحي",
      mod_health_desc: "اللقاحات، العلاجات، الزيارات البيطرية والتذكيرات — لكل حيوان.",
      open: "فتح",
    },
    clients_list: {
      overline_owner: "كل الزبائن",
      overline_trainer: "زبائني",
      overline_signup: "حسابات جديدة في الانتظار",
      title_clients: "الزبائن",
      title_signup: "التسجيلات الذاتية",
      view_all: "← عرض كل الزبائن",
      search_placeholder: "بحث بالاسم أو البريد…",
      th_name: "الاسم",
      th_email: "البريد الإلكتروني",
      th_plan: "الباقة",
      th_billing: "الدورية",
      th_status: "الحالة",
      th_rate: "السعر الفعلي",
      billing_monthly: "شهري",
      billing_annual: "سنوي (-20٪)",
      empty_first: "لا يوجد زبائن. أنشئ أول حساب أعلاه.",
      empty_search: "لا نتائج لهذا البحث.",
      self_signup_badge: "تسجيل ذاتي",
      self_signup_title: "سجّل من الصفحة الرئيسية",
      action_activate: "تفعيل الحساب",
      action_suspend: "إيقاف",
      action_pending: "وضع في الانتظار",
      action_whatsapp: "التواصل عبر واتساب",
      new_client: "زبون جديد",
      create_title: "إنشاء حساب مربٍّ",
      create_desc: "سيتمكن الزبون من تسجيل الدخول بالبريد وكلمة المرور أدناه.",
      full_name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      initial_password: "كلمة المرور الأولية",
      min6: "6 أحرف على الأقل",
      billing: "الدورية",
      initial_status: "حالة الحساب الأولية",
      status_active_paid: "نشط (تم استلام الدفع)",
      status_pending_paid: "في انتظار الدفع",
      annual_minus_20: "سنوي (-20٪ تخفيض)",
      tarif_summary: "ملخص السعر",
      tarif_annual: "سنوي: {a} د.ت (بدلًا من {b} د.ت) — توفير {c} د.ت",
      tarif_monthly: "شهري: {a} د.ت / شهر",
      create_btn: "إنشاء الحساب",
      status_updated: "تم تحديث الحالة",
      client_created: "تم إنشاء الزبون",
      client_created_desc: "يمكن لـ {name} الآن تسجيل الدخول.",
    },
    trainers_list: {
      overline: "إدارة المدرّبين",
      title: "المدرّبون",
      subtitle: "أوقف مدرّبًا أو أعد ضبط كلمة مروره عند الحاجة. يبقى زبائنه ظاهرين في لوحتك.",
      th_clients: "الزبائن",
      empty: "لا يوجد مدرّبون.",
      suspend: "إيقاف المدرّب",
      reactivate: "إعادة التفعيل",
      reset_password: "إعادة تعيين كلمة المرور",
      new_trainer: "مدرّب جديد",
      create_title: "إنشاء مدرّب شريك",
      create_desc: "سيتلقى المدرّب هذه البيانات. يجب أن يغيّر كلمة مروره عند أول اتصال.",
      password_hint: "كلمة المرور الأولية (10 على الأقل، كبير، رقم، خاص)",
      create_btn: "إنشاء المدرّب",
      reset_title: "إعادة تعيين كلمة المرور",
      reset_desc: "ستحدد كلمة مرور جديدة لـ {name}. كلمة مروره القديمة لن تعمل.",
      new_password: "كلمة المرور الجديدة",
      reset_btn: "إعادة التعيين",
      suspended_toast: "تم إيقاف المدرّب",
      reactivated_toast: "تم إعادة تفعيل المدرّب",
      created_toast: "تم إنشاء المدرّب",
      reset_toast: "تم إعادة تعيين كلمة المرور",
      reset_toast_desc: "سيستخدم {name} كلمة المرور الجديدة.",
    },
    audit: {
      overline: "سجل التدقيق",
      title: "النشاط الإداري",
      subtitle: "كل الإجراءات الحساسة (إنشاء، تفعيل، إيقاف، تغيير كلمة المرور) مسجّلة هنا.",
      filter_placeholder: "تصفية بالبريد أو الإجراء أو IP…",
      th_date: "التاريخ",
      th_actor: "الفاعل",
      th_action: "الإجراء",
      th_target: "الهدف",
      th_ip: "IP",
      empty: "لا توجد إدخالات.",
      actions: {
        create_client: "إنشاء زبون",
        create_trainer: "إنشاء مدرّب",
        client_active: "تفعيل زبون",
        client_pending: "زبون في الانتظار",
        client_suspended: "إيقاف زبون",
        trainer_active: "إعادة تفعيل مدرّب",
        trainer_suspended: "إيقاف مدرّب",
        reset_trainer_password: "إعادة كلمة مرور مدرّب",
        change_password: "تغيير كلمة المرور",
        self_signup: "تسجيل ذاتي",
        payment_activation: "تفعيل بالدفع",
        "2fa_enabled": "تفعيل 2FA",
        "2fa_disabled": "تعطيل 2FA",
        "2fa_backup_code_used": "استخدام رمز احتياطي 2FA",
      },
    },
    security: {
      overline: "الأمان",
      title: "المصادقة الثنائية",
      subtitle: "أضف حاجزًا ثانيًا: رمز من 6 أرقام تُنشئه تطبيق على هاتفك عند كل اتصال.",
      status_on: "2FA مفعّلة على حسابك",
      status_off: "2FA غير مفعّلة بعد",
      desc_on: "اتصالاتك محمية برمز مؤقت من 6 أرقام.",
      desc_off: "نوصي بشدة بتفعيل 2FA، خصوصًا في تونس حيث خطر الاختراق مرتفع.",
      enable: "تفعيل 2FA",
      disable: "تعطيل 2FA",
      step1: "الخطوة 1",
      step1_title: "امسح رمز QR",
      step1_desc: "افتح Google Authenticator أو Authy أو Microsoft Authenticator على هاتفك وامسح هذا الرمز.",
      manual_key: "أو أدخل المفتاح يدويًا",
      step2: "الخطوة 2",
      step2_title: "أدخل الرمز من 6 أرقام",
      step2_desc: "بعد إضافة الحساب في تطبيقك، أدخل الرمز المُنشأ للتأكيد.",
      activating: "جاري التفعيل…",
      activate_btn: "تفعيل 2FA",
      backup_title: "رموز الاسترداد — احتفظ بها الآن",
      backup_desc_a: "تتيح لك هذه الرموز الـ 8 تسجيل الدخول إذا فقدت هاتفك. كل رمز يُستخدم مرة واحدة فقط. ",
      backup_desc_b: "لن تُعرض هذه الرموز مرة أخرى أبدًا.",
      download_txt: "تنزيل بصيغة .txt",
      saved_dismiss: "حفظت الرموز",
      disable_title: "تعطيل 2FA",
      disable_desc: "هذا الإجراء يُقلل من أمان حسابك. ستحتاج لإعادة تفعيله لاستعادة الحماية.",
      current_password: "كلمة المرور الحالية",
      current_code: "رمز 2FA الحالي أو رمز استرداد",
      disable_btn: "تعطيل",
      toast_enabled: "تم تفعيل 2FA",
      toast_disabled: "تم تعطيل 2FA",
    },
    animals: {
      overline: "قطيعي",
      title: "سجل الحيوانات",
      subtitle: "تابع كل رأس مواش: التعريف، النوع، العمر، الوزن، والحالة.",
      new_animal: "حيوان جديد",
      add_title: "إضافة حيوان",
      edit_title: "تعديل الحيوان",
      th_tag: "الرقم",
      th_name: "الاسم",
      th_species_breed: "النوع / السلالة",
      th_sex: "الجنس",
      th_birth: "الولادة",
      th_weight: "الوزن",
      th_status: "الحالة",
      species: { Bovins: "أبقار", Ovins: "أغنام", Caprins: "ماعز", Lapins: "أرانب", Autre: "أخرى" },
      all_species: "كل الأنواع",
      female: "أنثى",
      male: "ذكر",
      stat_total: "المجموع",
      stat_active: "النشطون",
      stat_sold: "المباعون",
      stat_dead: "النافقون",
      empty_first: "لا توجد حيوانات. انقر على «حيوان جديد» للبدء.",
      empty_search: "لا نتائج مع هذه المرشحات.",
      search_placeholder: "بحث (رقم، اسم، سلالة)…",
      tag_label: "المعرّف / الرقم*",
      name_label: "الاسم (اختياري)",
      species_label: "النوع*",
      breed_label: "السلالة",
      sex_label: "الجنس*",
      birth_label: "تاريخ الولادة",
      weight_label: "الوزن (كغ)",
      status_label: "الحالة",
      notes_label: "ملاحظات",
      notes_placeholder: "ملاحظات…",
      delete_title: "حذف هذا الحيوان؟",
      saved_toast: "تم تحديث الحيوان",
      created_toast: "تم تسجيل الحيوان",
      deleted_toast: "تم حذف الحيوان",
    },
    health: {
      overline: "صحة القطيع",
      title: "الدفتر الصحي",
      subtitle: "اللقاحات، العلاجات، الزيارات البيطرية والتذكيرات — لكل حيوان.",
      new_record: "تسجيل جديد",
      add_title: "إضافة إلى الدفتر الصحي",
      edit_title: "تعديل التسجيل",
      add_animal_first: "أضف حيوانًا أولًا",
      export_pdf_all: "تصدير PDF (الكل)",
      export_pdf_one: "تصدير PDF",
      exporting: "جاري الإنشاء…",
      pdf_generated: "تم إنشاء PDF",
      export_error: "تعذر التصدير",
      types: { vaccine: "تطعيم", treatment: "علاج", checkup: "زيارة بيطرية", disease: "مرض", other: "أخرى" },
      all_types: "كل الأنواع",
      all_animals: "كل الحيوانات",
      search_placeholder: "بحث (عنوان، منتج، طبيب، رقم…)",
      reminder_overdue: "متأخر",
      reminder_upcoming: "قادم — الـ 30 يومًا القادمة",
      reminder_more: "+ {n} أخرى…",
      th_date: "التاريخ",
      th_animal: "الحيوان",
      th_type: "النوع",
      th_title: "العنوان / المنتج",
      th_vet: "الطبيب البيطري",
      th_next: "التذكير التالي",
      th_cost: "التكلفة",
      stat_total: "المجموع",
      empty_first_with_animals: "لا توجد تسجيلات. انقر على «تسجيل جديد» للبدء.",
      empty_no_animals: "أضف حيوانًا أولًا في السجل، ثم أنشئ دفتره الصحي هنا.",
      empty_search: "لا نتائج مع هذه المرشحات.",
      animal_deleted: "محذوف",
      animal_label: "الحيوان*",
      animal_select_ph: "اختر حيوانًا",
      type_label: "النوع*",
      date_label: "التاريخ*",
      title_label: "العنوان*",
      title_ph: "مثال: تطعيم FMD، علاج طفيليات، فحص حمل…",
      product_label: "المنتج / الدواء",
      product_ph: "إيفرمكتين، أفتوفاكس…",
      dose_label: "الجرعة / الكمية",
      vet_label: "الطبيب البيطري",
      vet_ph: "د. بن صالح",
      cost_label: "التكلفة (د.ت)",
      next_label: "التذكير التالي",
      next_hint: "سيظهر تلقائيًا في التذكيرات القادمة.",
      notes_label: "ملاحظات",
      notes_ph: "ملاحظات، أعراض جانبية…",
      delete_title: "حذف هذا التسجيل؟",
      delete_body: "«{title}» بتاريخ {date}. هذا الإجراء لا رجعة فيه.",
      saved_toast: "تم تحديث التسجيل",
      created_toast: "تم إضافة التسجيل",
      deleted_toast: "تم حذف التسجيل",
      animal_required: "اختر حيوانًا.",
    },
    pay: {
      back: "العودة إلى فضائي",
      overline: "تفعيل فوري",
      title: "اختر صيغتك.",
      subtitle: "دفع آمن بالبطاقة البنكية عبر Stripe. التفعيل التلقائي عند التأكيد.",
      pay_now: "ادفع الآن",
      per_month: "في الشهر",
      annual: "سنوي (-20٪)",
      monthly: "شهري",
      footer: "الأسعار بالدينار التونسي — تُحوّل إلى USD عند الدفع التجريبي عبر Stripe. في الإنتاج، سيتوفر الدفع بالدينار مباشرة عبر Konnect.",
      verifying: "جاري التحقق من الدفع…",
      verifying_sub: "يرجى الانتظار بضع ثوانٍ.",
      paid_title: "تم تأكيد الدفع!",
      paid_desc: "حسابك الآن نشط. أهلًا بك في Mazra3ti.",
      goto_dashboard: "الوصول إلى فضائي",
      timeout_title: "الدفع قيد المعالجة",
      timeout_desc: "يستغرق الدفع وقتًا أطول من المعتاد للتأكيد. ستتلقى رسالة عند التفعيل. يمكنك أيضًا تحديث هذه الصفحة.",
      expired_title: "انتهت الجلسة",
      expired_desc: "انتهت جلسة الدفع. يمكنك إعادة المحاولة.",
      retry: "إعادة المحاولة",
      save_12: "وفّر 12 د.ت",
      save_36: "وفّر 36 د.ت",
      essentiel_feats: ["حتى 50 رأس", "دفتر صحي", "محمول بدون اتصال"],
      pro_feats: ["حيوانات غير محدودة", "التكاثر", "المخزون والحصص", "التحليلات"],
      e_m_sub: "في الشهر",
      e_a_sub: "في السنة بدلًا من 60 د.ت",
      p_m_sub: "في الشهر",
      p_a_sub: "في السنة بدلًا من 180 د.ت",
    },
    login: {
      side_overline: "فضاء الشريك والإدارة",
      side_title_a: "زبائنك،",
      side_title_b: "لوحة قيادتك.",
      side_desc: "فعّل وتابع ورافق كل مربٍّ من واجهة واحدة، بالفرنسية أو العربية.",
      title: "تسجيل الدخول",
      subtitle: "أدخل بيانات اعتماد الشريك للوصول إلى فضائك.",
      email: "البريد الإلكتروني",
      email_ph: "you@example.tn",
      password: "كلمة المرور",
      submit: "تسجيل الدخول",
      submitting: "جاري الاتصال…",
      twofa_title: "التحقق 2FA",
      twofa_desc: "أدخل الرمز من 6 أرقام من تطبيق المصادقة.",
      twofa_label: "رمز التحقق",
      twofa_ph: "123 456 أو رمز احتياطي XXXX-XXXX",
      twofa_hint: "يمكنك أيضًا إدخال رمز استرداد للاستخدام مرة واحدة.",
      twofa_verify: "تحقق من الرمز",
      twofa_verifying: "جاري التحقق…",
      twofa_back: "← العودة إلى الخطوة السابقة",
    },
    signup: {
      side_overline: "كن مربيًا متصلًا",
      side_title_a: "أنشئ حسابك",
      side_title_b: "في 30 ثانية.",
      side_desc: "وصول فوري — دون مصادقة. استمتع بشهر تجربة مجاني لاكتشاف كل الوحدات.",
      perks: ["تفعيل فوري دون انتظار", "شهر تجربة مجاني دون التزام", "تخفيض -20٪ عند الدفع السنوي", "دعم مباشر عبر واتساب"],
      title: "تسجيل مربٍّ",
      subtitle: "حسابك يُفعَّل فوريًا مع شهر تجربة مجاني.",
      full_name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      farm: "القطاع",
      plan: "الباقة",
      billing: "الدورية",
      submit: "إنشاء حسابي",
      submitting: "جاري الإنشاء…",
      have_account: "لديك حساب بالفعل؟",
      login_link: "سجّل دخولك",
      success_title: "أهلًا بك في Mazra3ti!",
      success_desc: "تم تفعيل حسابك. لديك شهر تجربة مجاني. ادخل فورًا إلى لوحة التحكم.",
      success_cta_dashboard: "الدخول إلى لوحة التحكم",
      already_have: "لدي حساب — تسجيل الدخول",
      consent_label_fr: "أوافق على",
      consent_cgu: "الشروط العامة للاستخدام",
      consent_and: "و",
      consent_privacy: "سياسة الخصوصية",
      consent_must_accept: "يجب الموافقة على الشروط لإنشاء حسابك.",
      rule_length: "10 أحرف على الأقل",
      rule_upper: "حرف كبير واحد",
      rule_digit: "رقم واحد",
      rule_special: "رمز خاص واحد",
      plan_e: "Essentiel — 5 د.ت / شهر (شهر تجربة مجاني)",
      plan_p: "Pro — 15 د.ت / شهر (شهر تجربة مجاني)",
      plan_d: "Domaine — حسب الطلب",
    },
    change_pw: {
      title_forced: "أمّن حسابك",
      title_normal: "تغيير كلمة المرور",
      subtitle_forced: "لأسباب أمنية، يجب تحديد كلمة مرور جديدة قبل الوصول إلى فضائك.",
      subtitle_normal: "اختر كلمة مرور قوية جديدة.",
      current: "كلمة المرور الحالية",
      new_pw: "كلمة المرور الجديدة",
      confirm: "تأكيد كلمة المرور الجديدة",
      submit: "تحديث كلمة مروري",
      submitting: "جاري التحديث…",
      mismatch: "كلمتا المرور غير متطابقتين.",
      toast_updated: "تم تحديث كلمة المرور",
    },
    whatsapp: {
      cta_said: "تواصل مع هيثم عبر واتساب",
      hello_said: "السلام عليك هيثم 👋",
      signup_msg: "السلام عليك هيثم، سجّلت للتو في Mazra3ti ({name}، قطاع {farm}). هل يمكنك مرافقتي للبدء؟",
      pending_msg: "السلام عليك هيثم، انتهت فترة التجربة المجانية. هل يمكنك مساعدتي لتفعيل اشتراكي؟",
    },
    trial: {
      badge: "تجربة مجانية",
      banner_title: "التجربة المجانية جارية",
      days_left_one: "تبقّى لك {n} يوم",
      days_left_many: "تبقّى لك {n} أيام",
      ends_today: "تنتهي تجربتك اليوم",
      banner_sub: "استمتع بكل الميزات. فعّل اشتراكك متى شئت.",
      activate_btn: "تفعيل اشتراكي",
      perks_title: "ما يشمله الإصدار التجريبي",
      perks: ["كل الوحدات مفتوحة", "بياناتك محفوظة", "دعم واتساب مضمّن"],
    },
    activity: {
      th_last_login: "آخر اتصال",
      never: "لم يتصل",
      today: "اليوم",
      yesterday: "أمس",
      days_ago: "منذ {n} أيام",
      inactive: "غير نشط",
      send_reminder: "إرسال تذكير",
      reminder_sent_title: "تم إرسال التذكير",
      reminder_sent: "تم إرسال تذكير إلى {name}.",
      reminder_sent_mocked: "تم حفظ التذكير (وضع تجريبي، بدون Twilio).",
    },
    expert: {
      title: "نصائح تقنية للتربية",
      subtitle: "اطرح أسئلتك على سيف الدين، مهندسنا الفلاحي (إدارة المزرعة والتغذية).",
      whatsapp_card: "سؤال مكتوب (واتساب)",
      whatsapp_desc: "إدارة، تغذية، تكاثر — رد خلال 24 ساعة.",
      call_card: "اطلب إعادة الاتصال",
      call_desc: "سيف الدين سيتصل بك للمناقشة.",
      remaining: "تبقى لك {n}/{max} هذا الشهر",
      modal_title_whatsapp: "اطرح سؤالًا على سيف الدين",
      modal_title_call: "اطلب إعادة الاتصال",
      subject_label: "الموضوع",
      subject_ph: "مثال: ما هي الحصة الغذائية للنعاج الحوامل؟",
      message_label: "سؤالك",
      message_ph: "اشرح وضعيتك: عمر الحيوانات، التغذية الحالية، السياق…",
      submit: "إرسال",
      submitting: "جاري الإرسال…",
      success_title: "تم إرسال الطلب ✓",
      success_desc: "سيف الدين سيرد عليك عبر واتساب خلال 24 ساعة.",
      locked_title: "خدمة مميزة — باقة Pro",
      locked_desc_trial: "أثناء التجربة المجانية هذه الخدمة غير متاحة. فعّل اشتراك Pro (15 د.ت/شهر) للوصول إليها.",
      locked_desc_plan: "اشترك في باقة Pro (15 د.ت/شهر) للاستفادة من 5 أسئلة واتساب + 5 مكالمات/شهر مع سيف الدين.",
      upgrade_btn: "تفعيل باقة Pro",
      quota_exhausted: "تم الوصول إلى الحصة الشهرية — التجديد في الأول من الشهر القادم.",
      legal_disclaimer: "⚠️ هام: سيف الدين مهندس فلاحي، وليس طبيبًا بيطريًا. لا يمكنه وصف أدوية أو إجراء تشخيص طبي. لأي مرض أو إصابة أو وصفة، استشر طبيبًا بيطريًا مرخّصًا.",
    },
    repro: {
      overline: "تكاثر القطيع",
      title: "التكاثر",
      subtitle: "السفاد، التلقيح الاصطناعي، الحمل، الولادات والنسب — كل دورة التكاثر في لمحة واحدة.",
      new_event: "حدث جديد",
      add_title: "حدث تكاثر جديد",
      edit_title: "تعديل الحدث",
      add_animal_first: "أضف أنثى أولًا",
      mod_title: "التكاثر",
      mod_desc: "تابع الشياع، السفاد/التلقيح، الحمل والولادات. حساب تلقائي لتاريخ الولادة.",
      no_females_warning: "لا توجد إناث في قطيعك. أضف أنثى في السجل للبدء.",
      types: {
        breeding: "سفاد / تلقيح",
        pregnancy_check: "تشخيص حمل",
        birth: "ولادة",
        abortion: "إجهاض",
        weaning: "فطام",
      },
      methods: { natural: "سفاد طبيعي", ai: "تلقيح اصطناعي" },
      outcomes: {
        pending: "بانتظار التشخيص",
        confirmed_pregnant: "حامل مؤكدة",
        not_pregnant: "غير حامل",
        birth_done: "تمّت الولادة",
        aborted: "إجهاض",
      },
      all_types: "كل الأنواع",
      all_females: "كل الإناث",
      stat_total: "المجموع",
      stat_active_pregnancies: "حالات الحمل الجارية",
      reminder_overdue: "ولادات متأخرة",
      reminder_upcoming: "ولادات قادمة — 30 يومًا",
      reminder_more: "+ {n} أخرى…",
      search_placeholder: "بحث (رقم، فحل، طبيب…)",
      th_date: "التاريخ",
      th_female: "الأنثى",
      th_type: "النوع",
      th_method: "الطريقة / التفاصيل",
      th_partner: "الفحل / المانح",
      th_outcome: "الحالة",
      th_due: "الولادة المتوقعة",
      th_offspring: "المواليد",
      empty_first_with_females: "لا توجد أحداث. انقر على «حدث جديد» للبدء.",
      empty_search: "لا نتائج مع هذه المرشحات.",
      female_label: "الأنثى*",
      female_select_ph: "اختر أنثى",
      female_only: "تظهر الإناث فقط في القائمة.",
      type_label: "نوع الحدث*",
      date_label: "التاريخ*",
      method_label: "الطريقة (سفاد/تلقيح)",
      partner_tag_label: "الفحل / المانح (رقم أو اسم)",
      partner_ph: "TN-M015، Bull-Said-Holstein…",
      expected_birth_label: "الولادة المتوقعة",
      expected_birth_hint: "تُحسب تلقائيًا حسب النوع. قابلة للتعديل.",
      outcome_label: "الحالة",
      offspring_count_label: "عدد المواليد",
      offspring_alive_label: "المواليد الأحياء",
      vet_label: "الطبيب البيطري",
      vet_ph: "د. بن صالح",
      cost_label: "التكلفة (د.ت)",
      notes_label: "ملاحظات",
      notes_ph: "صعوبات، مضاعفات، ملاحظات…",
      delete_title: "حذف هذا الحدث؟",
      delete_body: "حدث «{type}» بتاريخ {date}. هذا الإجراء لا رجعة فيه.",
      saved_toast: "تم تحديث الحدث",
      created_toast: "تم تسجيل الحدث",
      deleted_toast: "تم حذف الحدث",
      female_required: "اختر أنثى.",
    },
    calendar: {
      overline: "أجندة الشهر",
      title: "التقويم",
      subtitle: "كل تذكيراتك الصحية والولادات القادمة، في لمحة واحدة.",
      mod_title: "التقويم",
      mod_desc: "عرض شهري لكل الأحداث القادمة (لقاحات، علاجات، ولادات).",
      prev: "الشهر السابق",
      next: "الشهر التالي",
      today: "اليوم",
      no_events: "لا توجد أحداث في هذا اليوم.",
      legend_health: "الدفتر الصحي",
      legend_birth: "الولادة",
      days_short: ["إ", "ث", "ر", "خ", "ج", "س", "ح"],
      months: ["جانفي", "فيفري", "مارس", "أفريل", "ماي", "جوان", "جويلية", "أوت", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"],
      open_module: "فتح",
      today_label: "اليوم",
    },
    nutrition: {
      overline: "تغذية القطيع",
      title: "التغذية",
      subtitle: "تابع مخزونك (الصوامع، العلف، المركزات) وتكلفة التغذية لكل حيوان.",
      mod_title: "التغذية",
      mod_desc: "الحصص، المخزون والتكلفة لكل حيوان. خصم تلقائي من الصومعة.",
      tab_stocks: "المخزون",
      tab_feedings: "الحصص",
      tab_calculator: "حاسبة الحصص",
      new_stock: "مخزون جديد",
      new_feeding: "حصة جديدة",
      add_stock_title: "إضافة مخزون",
      add_feeding_title: "توزيع حصة",
      types: { concentre: "مركّز", fourrage: "علف أخضر", foin: "تبن", paille: "قش", ensilage: "سيلاج", mineraux: "أملاح معدنية", autre: "أخرى" },
      stat_total_stocks: "المخزونات المسجّلة",
      stat_stock_value: "قيمة المخزون",
      stat_total_feedings: "الحصص الموزَّعة",
      stat_feed_cost: "تكلفة التغذية",
      stat_total_quantity: "الكمية الموزَّعة",
      stocks_empty: "لا يوجد مخزون. انقر على «مخزون جديد» للبدء.",
      feedings_empty: "لم يتم توزيع أي حصة بعد.",
      add_animal_first: "أضف حيوانًا أولًا",
      th_name: "الاسم", th_type: "النوع", th_quantity: "الكمية", th_cost_per_kg: "التكلفة/كغ",
      th_value: "القيمة", th_supplier: "المورِّد", th_animal: "الحيوان", th_feed: "العلف",
      th_date: "التاريخ", th_qty: "الكمية (كغ)", th_cost: "التكلفة",
      name_label: "اسم المخزون*", name_ph: "مركّز حلوب، تبن سُلّى…",
      type_label: "النوع*", quantity_label: "الكمية المتوفرة (كغ)*",
      cost_per_kg_label: "التكلفة لكل كغ (د.ت)*", supplier_label: "المورِّد",
      supplier_ph: "تعاونية الفتح…", animal_label: "الحيوان*",
      animal_select_ph: "اختر حيوانًا", feed_item_label: "المخزون المستخدم",
      feed_item_optional: "(أو اسم حر أدناه)", feed_item_none: "— بدون مخزون —",
      feed_name_label: "اسم حر (إذا لا يوجد مخزون)", feed_name_ph: "رعي خارجي، شراء ظرفي…",
      date_label: "التاريخ*", qty_label: "الكمية الموزَّعة (كغ)*",
      cost_label_override: "التكلفة (د.ت، اختياري)",
      cost_hint: "إذا تُرك فارغًا، يُحسب تلقائيًا من المخزون.",
      notes_label: "ملاحظات", notes_ph: "ملاحظات، وجبة، مجموعة…",
      saved_toast: "تم الحفظ", deleted_toast: "تم الحذف",
      delete_stock_title: "حذف هذا المخزون؟", delete_feeding_title: "حذف هذه الحصة؟",
      search_placeholder: "بحث (حيوان، علف، مورِّد)…",
    },
    husbandry: {
      tabs: { weighing: "الوزن", famacha: "فاماشا", hoof_trim: "الأظلاف", shearing: "الجَزّ", antiparasitic: "ضدّ الطفيليات", lineage: "النسب" },
      common: { date: "التاريخ", notes: "ملاحظات (اختياري)", notes_ph: "الحالة العامة، العلاج المطبّق…", add: "إضافة", save: "حفظ", delete_confirm: "حذف هذا الحدث؟", deleted: "تمّ الحذف", saved: "تمّ الحفظ ✓", error: "خطأ", no_events: "ما فمّاش أحداث مسجّلة.", how_to: "كيفاش نعمل", info_btn: "علاش و الأدوات", guide_btn: "الدليل الكامل", guide_period: "الدليل و الفترة" },
      weighing: {
        title: "الوزن",
        stat_count: "الوزنات", stat_current: "الوزن الحالي", stat_delta: "التطوّر",
        stat_gmq: "متوسط الكسب اليومي",
        gmq_unit: "غ/يوم", gmq_short: "غ/ي",
        gmq_chart_label: "متوسط الكسب اليومي بين كلّ وزنة (غ/يوم)",
        form_weight: "الوزن (كغ)", form_weight_ph: "مثال: 48.5",
        weight_required: "الوزن مطلوب",
        chart_label: "منحنى التطوّر",
        add_btn: "إضافة",
      },
      famacha: {
        banner_title: "نقاط فاماشا — كشف الطفيليات",
        banner_desc: "افحص لون ملتحمة العين باش تكشف فقر الدم اللي تسبّبو الطفيليات الداخلية (Haemonchus).",
        choose_label: "اختار النقطة المطابقة للون اللي شفتو",
        action_label: "الإجراء الموصى بيه:",
        save_btn: "احفظ النقطة",
        choose_required: "اختار نقطة",
        levels: [
          { label: "أحمر فاقع", verdict: "ممتاز", action: "ما يحتاجش علاج. الحيوان في صحّة." },
          { label: "أحمر-وردي", verdict: "مزيان", action: "ما يحتاجش علاج." },
          { label: "وردي", verdict: "للمراقبة", action: "مراقبة دقيقة. أعطي مضادّ ديدان إذا كانت علامات أخرى." },
          { label: "وردي شاحب", verdict: "فقر دم متوسّط", action: "أعطي مضادّ ديدان فورًا. اتّصل بالبيطري إذا تواصل." },
          { label: "أبيض شاحب", verdict: "فقر دم حادّ", action: "🚨 طارئ — مضادّ ديدان + اتّصل بالبيطري." },
        ],
        guide_title: "كيفاش نعمل فحص فاماشا",
        why_title: "علاش؟",
        why_desc: "نظام فاماشا يكشف فقر الدم اللي يسبّبو Haemonchus contortus، طفيلي معوي يمصّ الدم. أداة مرجعية باش تقرّر إذا كان النعجة لازمها مضادّ ديدان.",
        how_title: "كيفاش نعمل؟",
        steps: [
          "ثبّت الحيوان بهدوء.",
          "حطّ الجفن السفلي إلى الأسفل بالإبهام.",
          "قارن اللون مع لوحة السلّم (1=أحمر فاقع → 5=أبيض).",
          "سجّل النقطة في Mazra3ti و طبّق الإجراء الموصى بيه.",
        ],
        tip: "💡 أعمل الاختبار على كذا حيوان من القطيع.",
      },
      hoof: {
        banner_title: "تقليم الأظلاف",
        banner_desc: "كلّ 4 إلى 6 شهور باش تتجنّب العرج. الحيوان لازم يكون هادي و نظيف.",
        add_btn: "علّم التقليم كأنّو تعمل",
        event_label: "تقليم تعمل",
        guide_title: "تقليم الأظلاف — الدليل الكامل",
        steps_title: "الخطوات",
        tools_title: "الأدوات اللازمة",
        warning: "⚠️ ما تقطعش حتّى اللحم الحيّ. إذا حصل نزيف: عقّم فورًا باليود.",
        tools: [
          { name: "مقصّ الأظلاف", desc: "لقطع القرن الزايد" },
          { name: "سكين الترتيب", desc: "لتسوية باطن الظلف" },
          { name: "مبرد", desc: "للتشطيب" },
          { name: "مطهّر يودي", desc: "تعقيم بعد القطع" },
          { name: "قفّازات", desc: "نظافة و حماية" },
        ],
        steps: [
          "ثبّت الحيوان.",
          "نظّف الظلف بالفرشاة.",
          "اقطع القرن الزايد بالمقصّ.",
          "سوّي الباطن بالسكين.",
          "شطّب بالمبرد.",
          "عقّم إذا لمست نسيج حسّاس.",
        ],
      },
      shearing: {
        banner_title: "الجَزّ",
        banner_desc: "الفترة المثالية: ماي – جوان. دائمًا في الجوّ الجاف.",
        form_wool: "الصوف المحصود (كغ)", form_wool_ph: "مثال: 2.5",
        form_notes_ph: "النوعية، الجزّاز…",
        add_btn: "احفظ الجَزّ",
        event_label: "الجَزّ",
        wool_label: "كغ صوف",
        guide_title: "دليل الجَزّ",
        periods_title: "الفترات الموصى بيها",
        tools_title: "الأدوات",
        steps_title: "الخطوات",
        periods: [
          { species: "الأغنام", period: "ماي – جوان", reason: "قبل الحرارة الشديدة." },
          { species: "الماعز الأنغورا", period: "مارس و سبتمبر", reason: "جَزّتين في السنة." },
        ],
        tools: ["مقصّ كهربائي مهني", "أمشاط و سكاكين احتياطية", "زيت للمقصّ", "ملاقط لإيقاف النزيف", "يود للتعقيم"],
        steps: [
          "اجزّ في الجوّ الجاف.",
          "حيوان هادي على المؤخّرة.",
          "ابدأ من البطن للظهر.",
          "ابق قريب من الجلد دون جرحو.",
          "افرز الصوف فورًا.",
        ],
      },
      antiparasitic: {
        banner_title: "حمّام ضدّ الطفيليات",
        banner_desc: "2 إلى 3 مرّات في السنة. أعد بعد 14 يوم باش تكسر دورة الطفيليات.",
        form_product: "المنتج المستعمل", form_product_ph: "مثال: Amitraz، Deltamethrine…",
        form_notes_ph: "الجرعة، الملاحظات…",
        add_btn: "احفظ",
        default_event: "حمّام ضدّ الطفيليات",
        guide_title: "الحمّام ضدّ الطفيليات — علاش و كيفاش",
        why_title: "علاش نعملو؟",
        why_desc: "محاربة الجرب و القمل و الحشرات. ضروري لتجنّب فقدان الوزن و الحكّة.",
        frequency_title: "التواتر الموصى به",
        frequency_desc: "2 إلى 3 مرّات في السنة: الربيع، الصيف، الخريف.",
        tools_title: "الأدوات",
        steps_title: "الخطوات",
        warning: "⚠️ المنتجات سامّة. استشير بيطريك للمنتج المناسب.",
        tools: ["حمّام أو رشّاش", "منتج ضدّ الطفيليات (بوصفة بيطرية)", "بدلة + قفّازات + قناع", "ماء نظيف"],
        steps: [
          "حضّر المحلول حسب الإرشادات.",
          "مرّر الحيوان في الحمّام، اغمر الرأس 2-3 ثواني.",
          "مدّة الغمر: 30 إلى 60 ثانية.",
          "أعد بعد 14 يوم.",
          "ما تعالجش الإناث الحاملة في آخر 15 يوم.",
        ],
      },
      lineage: {
        banner_title: "شجرة النسب (3 أجيال)",
        banner_desc: "تعرض الأبوين و الأجداد المسجّلين.",
        grand_mother: "الجدّة ♀", grand_father: "الجدّ ♂",
        mother: "الأمّ ♀", father: "الأب ♂",
        self: "هو/هي", child: "ابن/بنت",
        empty_node: "غير مسجّل",
        children_label: "النسل",
      },
    },
    analytics: {
      overline: "نظرة شاملة", title: "تحليلات ومردودية",
      subtitle: "مؤشرات أداء رئيسية لقيادة استغلالك: القطيع، الصحة، التكاثر والتكاليف.",
      mod_title: "التحليلات", mod_desc: "لوحة قيادة KPI: العدد، التكاليف، الحمل، المردودية. تصدير PDF كامل.",
      export_pdf: "تصدير تقرير PDF", exporting: "جاري الإنشاء…",
      section_herd: "القطيع", section_health: "الصحة", section_repro: "التكاثر",
      section_nutrition: "التغذية", section_costs: "التكاليف",
      animals_total: "الحيوانات المسجَّلة", animals_active: "النشطون",
      animals_sold: "المباعون", animals_dead: "النافقون",
      health_total: "الأعمال الصحية", health_cost: "تكلفة الصحة",
      repro_events: "الأحداث", repro_active_preg: "حالات الحمل الجارية",
      repro_births: "الولادات", repro_offspring: "المواليد الأحياء",
      nutri_stocks: "المخزون", nutri_value: "قيمة المخزون",
      nutri_feedings: "الحصص", nutri_cost: "تكلفة التغذية",
      cost_total: "إجمالي التكاليف التشغيلية", cost_per_animal: "متوسط التكلفة / حيوان نشط",
    },
    tour: {
      start_btn: "جولة إرشادية",
      skip: "تخطّي",
      previous: "السابق",
      next: "التالي",
      finish: "هيا بنا!",
      step_label: "الخطوة {n} من {total}",
      welcome_title: "مرحبًا بك في Mazra3ti 🌾",
      welcome_desc: "في 60 ثانية، اكتشف 6 وحدات ستحوّل إدارة مزرعتك.",
      steps: [
        { title: "سجل الحيوانات", desc: "نقطة الانطلاق. سجّل كل رأس: الرقم، النوع، السلالة، الجنس، العمر، الوزن. هذا أساس كل شيء." },
        { title: "الدفتر الصحي", desc: "أدخل اللقاحات، العلاجات والزيارات البيطرية. التذكيرات تظهر تلقائيًا. صدّر PDF للمراقبة." },
        { title: "التكاثر", desc: "تابع السفاد، التلقيح، الحمل والولادات. تاريخ الولادة يُحسب تلقائيًا حسب النوع." },
        { title: "التقويم الموحّد", desc: "كل تذكيراتك الصحية والولادات في عرض شهري واحد. لن تنسى شيئًا." },
        { title: "التغذية", desc: "أدر صوامعك ووزّع الحصص. التكلفة لكل حيوان تُحسب لحظيًا، والمخزون يتحدّث تلقائيًا." },
        { title: "التحليلات والمردودية", desc: "كل مؤشراتك في مكان واحد: العدد، التكاليف، الحمل، المردودية. تقرير PDF قابل للتصدير." },
      ],
    },
  },
  pwa: {
    install_title: "ثبّت Mazra3ti",
    install_desc: "أضف التطبيق إلى شاشتك الرئيسية للوصول السريع، حتى دون اتصال بالإنترنت.",
    install_cta: "تثبيت",
    install_dismiss: "لاحقًا",
    ios_hint: "على iPhone: اضغط على مشاركة ثم «إضافة إلى الشاشة الرئيسية».",
    update_title: "نسخة جديدة متوفرة",
    update_desc: "تحديث لتطبيق Mazra3ti جاهز للتثبيت.",
    update_cta: "تحديث",
  },
};

// --- Deep merge helper for partial dialect overrides ---
function deepMerge(base, overrides) {
  if (!overrides) return base;
  if (Array.isArray(overrides)) return overrides;
  if (typeof base !== "object" || base === null) return overrides;
  if (typeof overrides !== "object") return overrides;
  const out = { ...base };
  for (const k of Object.keys(overrides)) out[k] = deepMerge(base[k], overrides[k]);
  return out;
}

// --- Tunisian Arabic (Tounsi) — partial overrides on top of MSA (ar) ---
// Only the most user-facing strings are dialectalized. Everything else inherits from `ar`.
const tnOverrides = {
  dir: "rtl",
  nav: {
    pricing: "الأسعار",
    why: "علاش Mazra3ti؟",
    contact: "إتصل بينا",
    login: "ادخل",
    cta: "ابدا بالماجان",
    home: "الرئيسية",
  },
  hero: {
    badge: "صنع في تونس · للمربيين التوانسة",
    title_l1: "موش مجرد تطبيق…",
    title_l2: "هذا هو سلاحك في الفحص.",
    subtitle: "Mazra3ti يهز عليك التعب اليومي. تتبع، نظم، وكبّر دخلك من الغنم والمعز عندك.",
    cta_primary: "جرب ماجاناً (شهر كامل)",
    cta_secondary: "شوف الأسعار",
    trust_1: "تجربة ماجانية شهر",
    trust_2: "بدون كارت بنكية",
    trust_3: "إلغاء ساعة باش",
  },
  client: {
    welcome: "مرحبا",
    monthly: "في الشهر",
    annual: "في العام",
    trial_active: "تجربة مفعّلة",
    open: "افتح",
    install: "ركّب التطبيق",
    pay_now: "خلّص توا",
  },
  shell: {
    overview: "نظرة عامة",
    clients: "الحرفاء",
    trainers: "المكوّنين",
    security: "السلامة",
    audit: "السجل",
    landing: "صفحة البداية",
  },
  pricing: {
    title: "الأسعار",
    monthly: "شهري",
    annual: "سنوي",
    save_year: "وفّر %",
    most_popular: "الأكثر طلباً",
    cta_choose: "اختار هذي",
  },
  contact: {
    title: "خوذلنا برقية",
    name: "إسمك",
    email: "إيميلك",
    message: "الرسالة",
    send: "أرسل",
  },
  pwa: {
    install_title: "ركّب Mazra3ti في تلفونك",
    install_desc: "افتحه بسرعة من الشاشة الرئيسية، حتى من بدون نت.",
    install_cta: "ركّب التطبيق",
    later: "بعد",
    ios_hint: "في iPhone: انقر على Share ثم «إضافة إلى الشاشة الرئيسية».",
    update_title: "في نسخة جديدة",
    update_desc: "تحديث جديد جاهز.",
    update_cta: "حدّث",
  },
};

const tn = deepMerge(ar, tnOverrides);

const dictionaries = { fr, ar, tn };

const LanguageContext = createContext({
  lang: "fr",
  setLang: () => {},
  t: fr,
});

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "fr";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "tn_lat") return "fr";
    return saved || "fr";
  });

  useEffect(() => {
    const dict = dictionaries[lang] || fr;
    document.documentElement.lang = lang;
    document.documentElement.dir = dict.dir;
    window.localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggle: () => setLang((l) => (l === "fr" ? "ar" : "fr")),
      t: dictionaries[lang] || fr,
    }),
    [lang]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}

export function useT() {
  return useContext(LanguageContext).t;
}
